import "@fontsource/press-start-2p/latin-400.css";
import "@fontsource/vt323/latin-400.css";
import { commit } from "virtual:lache-commit";
import { getCourseData, timedCourseNames } from "@lache/courses";
import { GamepadRoster, glyphs, KeyboardReader, PAD_SCHEMES, padScheme, TouchReader } from "@lache/input";
import { cleanCode, validCode } from "@lache/relay/room";
import {
  BUDGET_FRAME_MS,
  BUDGET_TICK_MS,
  clonePoses,
  defaultFeel,
  defaultPoses,
  HEADBANDS,
  headband,
  NEON,
  PerfMeter,
  PIXEL_FONT,
  PopupKind,
  type PoseName,
  ShapeFactory,
  TERM_FONT,
  TextOverlay,
} from "@lache/render";
import {
  atan2,
  BURNOUT_COURSE,
  BURNOUT_TITLE,
  type BurnoutWindow,
  burnoutNextWindow,
  type CourseData,
  cloneTuning,
  copyInputFrame,
  cos,
  createBurnoutProgress,
  createInputFrame,
  DT,
  defaultTuning,
  drainEvents,
  Ev,
  Grade,
  GradeKind,
  generateBurnoutWindow,
  HZ,
  type InputFrame,
  quantizeInputFrame,
  Run,
  RunCancel,
  runTape,
  type SimEvent,
  sin,
  type Tape,
  tuningHash,
  updateBurnoutProgress,
  validateCourse,
} from "@lache/sim";
import { Application, type Sprite } from "pixi.js";
import type { LacheApi, ScreenName } from "./api";
import { DEFAULT_APPEARANCE, readSavedAppearance, saveAppearance } from "./appearance";
import { AttractRace } from "./attract";
import { BestGhost } from "./bestghost";
import { BurnoutScores } from "./burnout-scores";
import { CharacterScreen } from "./character-screen";
import { GlobalLeaderboardScreen } from "./global-leaderboard";
import { GlobalScores } from "./global-scores";
import { GymTutorial } from "./gym-tutorial";
import { GymTutorialHud } from "./gym-tutorial-hud";
import {
  generateInfiniteWindow,
  INFINITE_CHUNK_LENGTH,
  INFINITE_COURSE,
  INFINITE_TITLE,
  type InfiniteWindow,
} from "./infinite-course";
import { InstallPrompt } from "./install";
import { InstallScreen } from "./install-screen";
import { Lobby, type LobbySeat } from "./lobby";
import { FixedStepLoop } from "./loop";
import { isTyping, ListMenu, type MenuItem } from "./menu";
import { MUSIC_LEVEL_NAMES, MUSIC_LEVELS, Music } from "./music";
import { OnlineScreen } from "./online/screen";
import { OnlineSession } from "./online/session";
import { setupLandscape, updateLandscapeViewport } from "./orientation";
import { loadSavedFeel, loadSavedPoses, loadSavedTuning, TuningPanel } from "./panel";
import { Player, type Seat, soloSeat } from "./player";
import { RecentTimes } from "./recent-times";
import { downloadJson } from "./recorder";
import { replayTuning } from "./replay-tuning";
import { formatWhen, newRunId, type RunPlayer, type RunRecord, RunStore, runRows } from "./runs";
import { courseKey, formatTime, type Placement, type ScoreEntry, ScoreStore } from "./scores";
import {
  ClockMode,
  type CourseChoice,
  CourseSelect,
  type LeaderboardChoice,
  LeaderboardScreen,
  type RaceLine,
  RaceResults,
  ResultsScreen,
  RunsScreen,
  type ScoreBoards,
} from "./screens";
import { loadSettings, MAX_PLAYERS, saveSettings } from "./settings";
import { type TelemetryExtras, telemetryText } from "./telemetry";
import {
  enterFullscreen,
  exitFullscreen,
  isFullscreen,
  isIos,
  isStandalone,
  prefersTouch,
  TouchControls,
} from "./touch";
import { UsernameScreen } from "./username";

const VERSION = "0.0.0-v2";
/** Authored practice courses follow the timed courses in the course select. */
const GYM = "gym";
const LACHE_PRACTICE = "lache-practice";
/** The relay of the online race (`packages/relay`, `docs/online.md`); `?relay=<url>` or `?relay=local` for one load. */
const RELAY_URL = "wss://lache-relay.brunerm99.workers.dev";
/** Real time between crossing the finish line and the results screen, in simulation ticks. */
const RESULTS_DELAY_TICKS = Math.round(1.2 * HZ);

function courseData(name: string): CourseData {
  const data = getCourseData(name);
  validateCourse(data);
  return data;
}

/** The dev overlays (telemetry, frame graph) are off unless F7 turned them on, which is remembered. */
const DEV_OVERLAYS_KEY = "lache.devOverlays";

function loadDevOverlays(): boolean {
  try {
    return localStorage.getItem(DEV_OVERLAYS_KEY) === "1";
  } catch {
    return false;
  }
}

function saveDevOverlays(on: boolean): void {
  try {
    if (on) localStorage.setItem(DEV_OVERLAYS_KEY, "1");
    else localStorage.removeItem(DEV_OVERLAYS_KEY);
  } catch {
    // Storage unavailable: the choice lasts until reload.
  }
}

function knownCourse(name: string | null): name is string {
  return name !== null && (name === GYM || soloOnly(name) || timedCourseNames.includes(name));
}

function soloPractice(name: string): boolean {
  return name === LACHE_PRACTICE || name === INFINITE_COURSE;
}

function soloOnly(name: string): boolean {
  return soloPractice(name) || name === BURNOUT_COURSE;
}

function streamedCourse(name: string): boolean {
  return name === INFINITE_COURSE || name === BURNOUT_COURSE;
}

function newPracticeSeed(): number {
  return crypto.getRandomValues(new Uint32Array(1))[0]!;
}

/** The help line for the touch controls, available in Pause in touch mode. */
const TOUCH_HELP =
  "Left thumb: slide to run or swing, up to jump, down to tuck. Hold GRAB to catch; lift to let go. To link, slide GRAB to RELEASE without lifting, then slide back to GRAB during flight. TUCK + forward slide rolls.";

/** "Ana", "Ana and Bo", "Ana, Bo and Cy": a list that reads as a sentence however many names. */
function nameList(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** "1st", "2nd", "3rd", "4th". */
function ordinal(n: number): string {
  const suffix = n === 1 ? "st" : n === 2 ? "nd" : n === 3 ? "rd" : "th";
  return `${n}${suffix}`;
}

function pickAxis(a: number, b: number): number {
  return Math.abs(a) >= Math.abs(b) ? a : b;
}

/** Every player's live frame at once, for the menus: buttons OR together, axes take the larger. */
function mergeFrames(out: InputFrame, players: readonly Player[]): void {
  const first = players[0];
  if (!first) return;
  copyInputFrame(out, first.liveInput);
  for (let i = 1; i < players.length; i++) {
    const f = players[i]!.liveInput;
    out.moveX = pickAxis(out.moveX, f.moveX);
    out.moveY = pickAxis(out.moveY, f.moveY);
    out.tiltX = pickAxis(out.tiltX, f.tiltX);
    out.tiltY = pickAxis(out.tiltY, f.tiltY);
    out.reach = Math.max(out.reach, f.reach);
    out.pull = Math.max(out.pull, f.pull);
    out.jump = out.jump || f.jump;
    out.tuck = out.tuck || f.tuck;
    out.pause = out.pause || f.pause;
    out.restart = out.restart || f.restart;
  }
}

function cssColor(color: number): string {
  return `#${color.toString(16).padStart(6, "0")}`;
}

/**
 * The pixel typefaces draw the popups and the START and FINISH labels as bitmap text, so they must
 * be in before any renderer is built: a glyph first drawn with the fallback face would stay that
 * way. Bundled with the game, they arrive at once; a browser without the fonts API, or a slow one,
 * moves on after a moment.
 */
async function loadFonts(): Promise<void> {
  if (typeof document === "undefined" || !("fonts" in document)) return;
  const wanted = [document.fonts.load(`64px "${PIXEL_FONT}"`), document.fonts.load(`20px "${TERM_FONT}"`)];
  const timeout = new Promise<void>((resolve) => setTimeout(resolve, 2500));
  await Promise.race([Promise.all(wanted).then(() => undefined), timeout]).catch(() => undefined);
}

async function boot(): Promise<void> {
  // Capture an install offer before font/renderer startup can yield to browser events.
  const installation = new InstallPrompt();
  const params = new URLSearchParams(location.search);
  const useSaved = !params.has("defaults");
  const courseParam = params.get("course");
  // Device capabilities, not the last input: a phone stays mobile after a keyboard or pad press.
  const mobileDevice = prefersTouch();
  // A link or a dev tool can open a course directly (?course=gym); otherwise the game opens on the
  // start menu, with the first timed course behind it.
  let courseName = knownCourse(courseParam) ? courseParam : (timedCourseNames[0] ?? GYM);
  const seedParam = Number(params.get("practiceSeed"));
  let practiceSeed =
    params.has("practiceSeed") && Number.isInteger(seedParam) && seedParam >= 0 && seedParam <= 0xffffffff
      ? seedParam
      : newPracticeSeed();
  let practiceWindow: InfiniteWindow | null =
    courseName === INFINITE_COURSE ? generateInfiniteWindow(practiceSeed) : null;
  let practiceDistance = 0;
  let burnoutWindow: BurnoutWindow | null = courseName === BURNOUT_COURSE ? generateBurnoutWindow() : null;
  let burnoutProgress = createBurnoutProgress();
  let burnoutRestartHeld = false;
  let burnoutLast: { obstacles: number; time: number; duration: number } | null = null;
  let course = burnoutWindow?.course ?? practiceWindow?.course ?? courseData(courseName);

  const tuning = useSaved ? loadSavedTuning(defaultTuning) : cloneTuning(defaultTuning);
  const feel = useSaved ? loadSavedFeel(defaultFeel) : { ...defaultFeel };
  const poses = useSaved ? loadSavedPoses(defaultPoses) : clonePoses(defaultPoses);
  const viewParam = Number(params.get("view"));
  if (Number.isFinite(viewParam) && viewParam > 2) {
    feel.viewWidth = viewParam;
    feel.viewWidthSplit = viewParam;
  }
  // ?scheme=N picks the pad scheme for this load (see docs/controls.md); the F1 panel can change it later.
  const schemeParam = Number(params.get("scheme"));
  if (params.has("scheme") && Number.isInteger(schemeParam)) tuning.padScheme = schemeParam;
  // The game settings (the ghost, the seats' schemes and headbands): saved, with ?ghost=0|1 for this load.
  const settings = useSaved ? loadSettings(undefined, PAD_SCHEMES.length) : loadSettings(null);
  let appearance = useSaved ? readSavedAppearance() : null;
  if (appearance) {
    const band = HEADBANDS.findIndex((entry) => cssColor(entry.color) === appearance!.headbandColor);
    if (band >= 0) settings.bands[0] = band;
  }
  if (params.get("ghost") === "0") settings.ghost = false;
  else if (params.get("ghost") === "1") settings.ghost = true;

  const gameEl = document.getElementById("game")!;
  const hud = document.getElementById("hud")!;
  const promptEl = document.getElementById("prompt")!;

  setupLandscape(params.get("touch") === "1" || (params.get("touch") !== "0" && mobileDevice));

  await loadFonts();
  const app = new Application();
  await app.init({
    background: NEON.night,
    resizeTo: gameEl,
    antialias: true,
    resolution: Math.min(2, window.devicePixelRatio || 1),
    autoDensity: true,
    preference: "webgl",
    powerPreference: "high-performance",
  });
  app.ticker.stop();
  gameEl.appendChild(app.canvas);
  // The tube over the game: scanlines and dark corners, a setting (`?crt=0|1` for one load).
  const crtEl = document.createElement("div");
  crtEl.className = "crt";
  gameEl.appendChild(crtEl);
  if (params.get("crt") === "0") settings.crt = false;
  else if (params.get("crt") === "1") settings.crt = true;
  function applyCrt(): void {
    document.documentElement.classList.toggle("crt", settings.crt);
  }
  applyCrt();
  // The synthwave loop under everything (`music.ts`), a setting with ?music=0..3 for one load. A
  // browser keeps audio silent until the player has pressed something, so the first press starts it.
  const musicParam = Number(params.get("music"));
  if (params.has("music") && Number.isInteger(musicParam)) {
    settings.musicLevel = Math.min(MUSIC_LEVELS.length - 1, Math.max(0, musicParam));
  } else if (!useSaved) {
    // `?defaults` is the clean-slate flag the test suite loads with: it plays silent unless a run
    // asks for the music by name, so a screenshot or a timing test never has a synth under it.
    settings.musicLevel = 0;
  }
  const music = new Music();
  music.setLevel(settings.musicLevel);
  // A browser lets audio start on a key, a mouse press, or the end of a touch: a touch's start (and
  // a pointerdown from a finger) is not a gesture it counts, which kept phones silent.
  for (const event of ["keydown", "pointerdown", "pointerup", "touchend", "click"] as const) {
    window.addEventListener(event, () => music.resume(), { passive: true, capture: true });
  }
  window.addEventListener("gamepadconnected", () => music.resume());
  document.addEventListener("visibilitychange", () => music.setPageVisible(!document.hidden));
  window.addEventListener("pagehide", () => music.setPageVisible(false));
  window.addEventListener("pageshow", () => music.setPageVisible(!document.hidden));

  const shapes = new ShapeFactory(app.renderer);
  type RendererMode = "debug" | "silhouette" | "both";
  let rendererMode: RendererMode = (params.get("renderer") as RendererMode | null) ?? "silhouette";

  // ---- Devices. One keyboard reader and one touch reader for the page; each player's input source
  // takes the keyboard when its seat holds it, its own pad by index, and (the first player) the touch
  // controls. The roster watches every pad for the join screen.
  const keyboard = new KeyboardReader();
  keyboard.attach(window);
  const touchReader = new TouchReader();
  touchReader.config.lacheAssist = params.get("touchassist") === "0" ? false : settings.touchLacheHelp;
  const roster = new GamepadRoster();

  // Touch mode shows the on-screen controls: on at load on a phone or tablet (?touch=1 forces it,
  // ?touch=0 keeps it off), on again whenever a finger touches the screen, off when a key or a pad
  // button is pressed, so each device brings up its own controls.
  const touchParam = params.get("touch");
  // ?touchdive=0 turns the phone's automatic dive off for one load (the touch golden test replays a tape).
  if (params.get("touchdive") === "0") touchReader.config.autoDive = false;
  let touchMode = touchParam === "1" || (touchParam !== "0" && mobileDevice);
  const touchControls = new TouchControls(document.body, touchReader, {
    toggleFullscreen: () => (isFullscreen() ? exitFullscreen() : enterFullscreen()),
  });
  const rootClasses = document.documentElement.classList;
  rootClasses.toggle("ios", isIos());
  rootClasses.toggle("standalone", isStandalone());
  const menuTouchPointers = new Map<number, "held" | "released">();
  let pendingTouchMode: boolean | null = null;
  function clearReleasedMenuTouches(): void {
    for (const [id, phase] of menuTouchPointers) {
      if (phase === "released") menuTouchPointers.delete(id);
    }
  }
  function applyTouchUi(): void {
    rootClasses.toggle("touch", touchMode);
    touchControls.show(touchMode && !overlayOpen());
  }
  function setTouchMode(on: boolean): void {
    if (touchParam === "1" || touchParam === "0") return;
    // A new keyboard/pad action also ends a gesture whose browser click was cancelled.
    if (!on) clearReleasedMenuTouches();
    // Changing row heights during a menu tap can move a different button under the finger.
    if (menuTouchPointers.size > 0) {
      pendingTouchMode = on;
      return;
    }
    pendingTouchMode = null;
    if (on === touchMode) return;
    touchMode = on;
    applyTouchUi();
  }
  window.addEventListener(
    "pointerdown",
    (e) => {
      if (e.pointerType !== "touch") return;
      const controlsWereVisible = touchControls.visible;
      if (
        overlayOpen() ||
        menuTouchPointers.size > 0 ||
        (e.target as Element | null)?.closest?.(".gym-tutorial-toggle")
      ) {
        clearReleasedMenuTouches();
        menuTouchPointers.set(e.pointerId, "held");
      }
      setTouchMode(true);
      if (!controlsWereVisible && touchControls.visible) touchControls.adoptPointer(e);
    },
    { capture: true },
  );
  function applyPendingTouchMode(): void {
    if (menuTouchPointers.size === 0 && pendingTouchMode !== null) setTouchMode(pendingTouchMode);
  }
  const releaseMenuTouch = (e: PointerEvent): void => {
    if (!menuTouchPointers.has(e.pointerId)) return;
    // A slow browser can render between pointerup and its synthesized click. Wait for the click
    // itself; non-primary fingers and cancelled gestures do not produce one.
    if (e.type === "pointerup" && e.isPrimary) menuTouchPointers.set(e.pointerId, "released");
    else menuTouchPointers.delete(e.pointerId);
    applyPendingTouchMode();
  };
  window.addEventListener("pointerup", releaseMenuTouch, { capture: true });
  window.addEventListener("pointercancel", releaseMenuTouch, { capture: true });
  window.addEventListener("click", () => {
    clearReleasedMenuTouches();
    applyPendingTouchMode();
  });
  // Nothing stays held while the page is away (a notification, the app switcher, a call); a run on a
  // phone comes back paused.
  function cancelTouchGestures(): void {
    touchReader.cancelAll();
    menuTouchPointers.clear();
    applyPendingTouchMode();
  }
  window.addEventListener("blur", cancelTouchGestures);
  // iOS can leave the page scrolled after its keyboard closes (typing a name); put the game back.
  document.addEventListener("focusout", () => {
    if (touchMode) window.scrollTo(0, 0);
  });

  const scores = new ScoreStore();
  const runs = new RunStore();
  // Local development and clean-slate tools never publish to production. An explicit local URL
  // exercises the same score API in e2e; the installed/public build uses the existing Worker.
  const scoresParam = params.get("scores");
  const scoreBaseURL =
    scoresParam === "0"
      ? null
      : scoresParam || (import.meta.env.PROD && useSaved ? RELAY_URL.replace(/^ws/, "http") : null);
  const globalScores = new GlobalScores(scoreBaseURL);
  const burnoutScores = new BurnoutScores(scoreBaseURL);
  const burnoutBoard = (): ScoreEntry[] =>
    burnoutScores.localBoard().map((entry) => ({ ...entry, falls: 1, splits: [] }));
  const scoreBoards: ScoreBoards = {
    get enabled() {
      return globalScores.enabled;
    },
    get pendingCount() {
      return globalScores.pendingCount + burnoutScores.pendingCount;
    },
    flush: async () => {
      await Promise.all([globalScores.flush(), burnoutScores.flush()]);
    },
    load: async (name) =>
      name === BURNOUT_COURSE
        ? (await burnoutScores.load()).map((entry) => ({ ...entry, falls: 1, splits: [] }))
        : globalScores.load(name),
  };
  let scoreGeneration = 0;
  /** A replayed player gets nothing once its tape ends. */
  const NEUTRAL_INPUT = createInputFrame();
  const perf = new PerfMeter(hud);
  const telemetry = new TextOverlay(hud, "telemetry");
  const perfText = new TextOverlay(hud, "perftext");
  const rawPanel = new TextOverlay(hud, "raw");
  rawPanel.show(false);
  const banner = document.createElement("div");
  banner.className = "banner";
  banner.style.display = "none";
  hud.appendChild(banner);

  // ---- Players and their viewports. A single player fills the canvas; a split-screen race stacks
  // one viewport per seat (top to bottom), with a line between them.
  let players: Player[] = [];
  /** The lines between the viewports of a race: a row boundary, and the columns inside a row. */
  const dividers: Sprite[] = [0, 1, 2, 3].map(() => {
    const d = shapes.rect(app.stage, 0, 0, 1, 1, NEON.pink);
    d.visible = false;
    return d;
  });
  /** How the current game is played: one player over the whole screen, a race with a seat per half, the replay of a saved run, or an online race (one player here, the others as ghosts fed over the network). */
  let mode: "single" | "split" | "replay" | "online" = "single";
  let gymTutorial: GymTutorial | null = null;
  let tutorialPadKey = "";
  let tutorialPadGlyphs = glyphs("unknown");
  // A sibling of the touch layer: the fixed #hud creates its own stacking context below it.
  const gymTutorialHud = new GymTutorialHud(document.body, () => toggleGymTutorial());

  function toggleGymTutorial(): void {
    settings.gymTutorial = !settings.gymTutorial;
    gymTutorial?.resetAttempt();
    saveSettings(settings);
    updateGymTutorialHud();
  }

  function updateGymTutorialHud(): void {
    const first = players[0];
    const visible =
      courseName === GYM && mode === "single" && !!first && !first.recorder.playing && !overlayOpen();
    gymTutorialHud.show(visible);
    if (!visible || !first) return;
    const pad = first.input.gamepad;
    const usingPad = pad.snapshot.connected && pad.lastEdgeTime >= keyboard.lastEdgeTime;
    const padKey = `${pad.snapshot.family}:${first.cond.padScheme}`;
    if (padKey !== tutorialPadKey) {
      tutorialPadKey = padKey;
      tutorialPadGlyphs = glyphs(pad.snapshot.family, padScheme(first.cond.padScheme));
    }
    gymTutorialHud.update(
      settings.gymTutorial,
      gymTutorial?.view() ?? null,
      touchMode ? "touch" : usingPad ? "pad" : "keyboard",
      tutorialPadGlyphs,
      touchReader.config.lacheAssist,
    );
  }
  /** The seats of the split-screen race (from the join screen, or defaults for ?players=2). */
  let raceSeats: Seat[] = [];
  /** The replay: the run, the player whose time was picked, the players shown as cams (in player order), and their seats. */
  let replayOf: { record: RunRecord; player: number } | null = null;
  let replayPlayers: RunPlayer[] = [];
  let replaySeats: Seat[] = [];
  /** The record's players not shown as cams in a one-cam replay, run beside the cam as ghosts. */
  let replayGhosts: BestGhost[] = [];
  /** Tick at which the runs list comes back after every tape of a replay has ended, or -1. */
  let replayEndAt = -1;
  let replayStarting = false;
  /** The run saved last (a finish), for the results' replay; the race's record while it is being filled. */
  let lastRunId: string | null = null;
  let raceRecord: RunRecord | null = null;
  /** The player's fastest saved run on the course, run beside them in a time trial (`docs/replays.md`). */
  const bestGhost = new BestGhost(course, tuning);
  const menuInput = createInputFrame();

  const eventLog: SimEvent[] = [];
  const scratchEvents: SimEvent[] = [];
  let latencyMs = -1;
  let pendingEdge = 0;
  let lastEdgeSeen = 0;
  let playbackResolve: ((r: { hash: string; ticks: number; events: SimEvent[] }) => void) | null = null;
  let playbackEvents: SimEvent[] = [];
  /** Whether the tape being played records its finish on the scoreboard (tests do; a replay does not). */
  let playbackScores = false;
  let showRaw = false;
  /** The dev overlays (telemetry, frame graph) are hidden unless F7 (remembered) or ?dev shows them. */
  let devOverlays = params.has("dev") || loadDevOverlays();
  let padWasConnected = false;
  let padEdgeSeen = 0;
  let bannerUntil = 0;
  /** Tick at which the results screen opens after a finish, or -1. */
  let resultsAtTick = -1;
  let lastPlacement: Placement | null = null;
  let layoutW = 0;
  let layoutH = 0;

  function showBanner(text: string, ms = 1200): void {
    banner.textContent = text;
    banner.style.display = "";
    bannerUntil = performance.now() + ms;
  }

  function applyDevOverlays(): void {
    hud.classList.toggle("game", !devOverlays);
  }

  /** The pad scheme of a seat: the first follows the tuning (the controls menu, F6, F1), the rest the settings. */
  function seatScheme(index: number): number {
    return index === 0
      ? Math.round(tuning.padScheme)
      : (settings.schemes[index] ?? Math.round(tuning.padScheme));
  }

  function soloSeats(): Seat[] {
    return [soloSeat(settings.bands[0] ?? 0, seatScheme(0))];
  }

  /** Default race seats when the join screen is skipped (?players=N): the keyboard and the first pad, then each next pad. */
  function defaultRaceSeats(count: number): Seat[] {
    const seats: Seat[] = [];
    for (let i = 0; i < Math.min(MAX_PLAYERS, count); i++) {
      seats.push({
        pad: -1,
        slot: i,
        keyboard: i === 0,
        band: settings.bands[i] ?? i,
        scheme: seatScheme(i),
      });
    }
    return seats;
  }

  /** The join screen's seats as the game binds them: a pad by index, or the keyboard with no pad at all. */
  function lobbySeatsToRace(seats: LobbySeat[]): Seat[] {
    return seats.map((s, i) => ({
      pad: s.pad,
      slot: s.pad >= 0 ? 0 : -1,
      keyboard: s.keyboard,
      band: s.band,
      scheme: seatScheme(i),
    }));
  }

  function currentSeats(): Seat[] {
    return mode === "split" ? raceSeats : mode === "replay" ? replaySeats : soloSeats();
  }

  /** A remote player's name in an online race, by peer number. */
  function peerName(peer: number): string {
    return session.peers.get(peer)?.name ?? `Player ${peer + 1}`;
  }

  function sameSeat(a: Seat, b: Seat): boolean {
    return a.pad === b.pad && a.slot === b.slot && a.keyboard === b.keyboard;
  }

  /** The player's name: the saved name for the first seat, "Player 2" for the next; in a replay, the run's. */
  function playerName(p: Player): string {
    if (mode === "replay") return replayPlayers[p.index]?.name ?? "";
    return p.index === 0 ? scores.name : `Player ${p.index + 1}`;
  }

  function deviceLabel(seat: Seat): string {
    if (seat.pad >= 0) return roster.label(seat.pad);
    if (seat.slot < 0) return seat.keyboard ? "keyboard" : "no device";
    return seat.keyboard ? `keyboard or pad ${seat.slot + 1}` : `pad ${seat.slot + 1}`;
  }

  /** Build the players for `seats` (reusing them when only the look changed) on the current course. */
  function buildPlayers(seats: Seat[]): void {
    const same = players.length === seats.length && players.every((p, i) => sameSeat(p.seat, seats[i]!));
    if (!same) {
      for (const p of players) p.destroy();
      players = seats.map(
        (seat, i) =>
          new Player(
            i,
            seat,
            course,
            tuning,
            feel,
            poses,
            shapes,
            poses,
            keyboard,
            i === 0 ? touchReader : null,
            app.stage,
            hud,
          ),
      );
      for (const p of players) for (const q of players) if (q !== p) p.addGhost(q);
      for (const p of players) p.recorder.onPlaybackEnd = () => onTapeEnd(p);
      // The best-time ghost rides with the first player (a time trial's).
      players[0]?.addGhostSource("best", bestGhost, headband(0).color, "run");
      syncBestGhostLook();
      // The dividers draw over the viewports.
      for (const d of dividers) app.stage.addChild(d);
    }
    players.forEach((p, i) => {
      p.applySeat(seats[i]!);
      p.setAppearance(i === 0 && mode !== "replay" ? appearance : null);
      p.setTag(players.length > 1 ? `P${i + 1}` : null);
    });
    if (!same) captureClosingInputs();
    for (const p of players) for (const q of players) if (q !== p) p.syncGhost(q);
    layoutViewports();
    applyRendererMode();
    refreshRunHud();
  }

  /** Re-bind every player's seat (a scheme or a headband changed in the settings). */
  function refreshSeats(): void {
    if (mode === "split") raceSeats = raceSeats.map((s, i) => ({ ...s, scheme: seatScheme(i) }));
    if (players.length > 0) buildPlayers(currentSeats());
  }

  /**
   * How many cells each row of the screen holds, for a race of `n`. One player fills the screen;
   * two take a row each, because a course runs sideways and seeing ahead beats seeing up; three and
   * four take a grid, with a lone third player given the whole bottom row rather than a dead cell
   * beside them. Four is the most a screen holds before the ninja is too small to read.
   */
  function rowsFor(n: number): number[] {
    if (n <= 1) return [1];
    if (n === 2) return [1, 1];
    if (n === 3) return [2, 1];
    return [2, 2];
  }

  /** The rectangle of the canvas each player draws in, in seat order. Rounded so no seam is left. */
  function viewportCells(n: number, W: number, H: number): { x: number; y: number; w: number; h: number }[] {
    const rows = rowsFor(n);
    const cells: { x: number; y: number; w: number; h: number }[] = [];
    for (let r = 0; r < rows.length; r++) {
      const y = Math.round((r * H) / rows.length);
      const h = Math.round(((r + 1) * H) / rows.length) - y;
      const cols = rows[r]!;
      for (let c = 0; c < cols; c++) {
        const x = Math.round((c * W) / cols);
        cells.push({ x, y, w: Math.round(((c + 1) * W) / cols) - x, h });
      }
    }
    return cells.slice(0, n);
  }

  function layoutViewports(): void {
    const W = app.screen.width;
    const H = app.screen.height;
    layoutW = W;
    layoutH = H;
    const n = players.length;
    const cells = viewportCells(n, W, H);
    players.forEach((p, i) => {
      const cell = cells[i] ?? { x: 0, y: 0, w: W, h: H };
      // A cell that still spans the screen is a half; a narrower one is a quarter and needs its own
      // scale and camera rise, since it is half as wide for the same shape of course.
      const wide = cell.w > W * 0.9;
      const view = n <= 1 ? feel.viewWidth : wide ? feel.viewWidthSplit : feel.viewWidthQuad;
      p.viewport.setRect(cell.x, cell.y, cell.w, cell.h, view, feel, n > 1);
      p.viewport.camera.offsetY =
        n <= 1 ? feel.cameraRise : wide ? feel.cameraRiseSplit : feel.cameraRiseQuad;
      p.hudRoot.style.left = `${(100 * cell.x) / W}%`;
      p.hudRoot.style.width = `${(100 * cell.w) / W}%`;
      p.hudRoot.style.top = `${(100 * cell.y) / H}%`;
      p.hudRoot.style.height = `${(100 * cell.h) / H}%`;
      p.hudRoot.classList.toggle("split", n > 1);
      p.hudRoot.classList.toggle("quad", n > 1 && !wide);
    });
    // One line along every edge the cells share: the row boundaries, then the columns inside a row.
    let line = 0;
    const put = (x: number, y: number, w: number, h: number): void => {
      const d = dividers[line++];
      if (!d) return;
      d.visible = true;
      d.position.set(x, y);
      d.width = w;
      d.height = h;
    };
    if (n > 1) {
      const rows = rowsFor(n);
      for (let r = 1; r < rows.length; r++) put(0, Math.round((r * H) / rows.length) - 1, W, 2);
      for (let r = 0; r < rows.length; r++) {
        const y = Math.round((r * H) / rows.length);
        const h = Math.round(((r + 1) * H) / rows.length) - y;
        for (let c = 1; c < rows[r]!; c++) put(Math.round((c * W) / rows[r]!) - 1, y, 2, h);
      }
    }
    for (let i = line; i < dividers.length; i++) dividers[i]!.visible = false;
  }

  function applyRendererMode(): void {
    for (const p of players) {
      p.debug.setDynamicVisible(rendererMode !== "silhouette");
      // The window bands and split marks teach the gym; on a course they show only with the debug shapes.
      p.debug.setTellsVisible(!p.sim.world.timed || rendererMode !== "silhouette");
    }
  }

  // Controls menu: a select on the HUD for the pad scheme (F6 cycles it). It writes tuning.padScheme
  // through the panel so the choice saves and the F1 slider agrees.
  const controlsEl = document.createElement("label");
  controlsEl.className = "controls";
  controlsEl.textContent = "controls (F6) ";
  const schemeSelect = document.createElement("select");
  PAD_SCHEMES.forEach((s, i) => {
    const option = document.createElement("option");
    option.value = String(i);
    option.textContent = `${i}: ${s.name}`;
    schemeSelect.appendChild(option);
  });
  controlsEl.appendChild(schemeSelect);
  hud.appendChild(controlsEl);
  function syncSchemeSelect(): void {
    schemeSelect.value = String(Math.round(tuning.padScheme));
  }
  syncSchemeSelect();

  const panel = new TuningPanel({
    tuning,
    feel,
    poses,
    defaultTuning,
    defaultFeel,
    defaultPoses,
    onTuningChange: () => {
      for (const p of players) {
        const tape = mode === "replay" ? replayPlayers[p.index]?.tape : undefined;
        p.setTuning((tape && replayTuning(tape.tuningHash, tuning)) || tuning);
      }
      bestGhost.setTuning(tuning);
      loadBestGhost();
      for (const ghost of replayGhosts) ghost.setTuning(tuning);
      session.setTuning(tuning);
      syncSchemeSelect();
      refreshSeats();
    },
    onFeelChange: () => {
      layoutViewports();
      // The world's statics are drawn once from the feel (the grid, the edges): redraw them.
      for (const p of players) p.rebuildWorld(feel);
    },
    onPosesChange: () => {},
    onPreview: (pose: PoseName | null) => {
      for (const p of players) p.silhouette.solver.previewPose = pose;
    },
    onAction: (name) => {
      if (name === "restart at start") restartAtStart();
    },
  });

  // ---- Menus: start, course select, settings, pause, results, and the join screen. At most one is
  // open; the simulation waits while one is.

  const startMenu = new ListMenu(document.body, {
    className: "start-menu",
    title: "Lache",
    hint: "stick or arrows to choose, Jump or Enter to go",
    touchHint: "tap to choose",
  });
  const brand = document.createElement("div");
  brand.className = "brand";
  const brandTitle = document.createElement("div");
  brandTitle.className = "brand-title";
  brandTitle.setAttribute("aria-label", "LACHE");
  // One span per letter, so the letters can wave one after the other; each carries its own letter
  // for the extruded shadow the stylesheet draws behind it.
  [..."LACHE"].forEach((ch, i) => {
    const span = document.createElement("span");
    span.className = "ch";
    span.textContent = ch;
    span.dataset.ch = ch;
    span.style.setProperty("--i", String(i));
    brandTitle.appendChild(span);
  });
  const brandSub = document.createElement("div");
  brandSub.className = "brand-sub";
  brandSub.textContent = "ninja warrior";
  brand.append(brandTitle, brandSub);
  startMenu.content.appendChild(brand);
  const recentTimes = new RecentTimes(startMenu.content, globalScores);
  const characterScreen = new CharacterScreen(document.body, {
    onBack: () => {
      keyboard.cancelAll();
      openStartMenu();
    },
    onChange: (next) => {
      appearance = { ...next };
      const saved = saveAppearance(appearance);
      const band = HEADBANDS.findIndex((entry) => cssColor(entry.color) === next.headbandColor);
      if (band >= 0) {
        settings.bands[0] = band;
        if (mode === "split" && raceSeats[0]) raceSeats[0] = { ...raceSeats[0], band };
        saveSettings(settings);
        session.setBand(band);
      }
      refreshSeats();
      return saved;
    },
  });

  function syncAppearanceBand(band: number): void {
    if (!appearance) return;
    appearance = { ...appearance, headbandColor: cssColor(headband(band).color) };
    if (mode === "split" && raceSeats[0]) raceSeats[0] = { ...raceSeats[0], band };
    saveAppearance(appearance);
  }

  const selectMenu = new ListMenu(document.body, {
    className: "select-menu",
    title: "Courses",
    hint: "stick or arrows to choose, Jump or Enter to play, Escape to go back",
    touchHint: "tap a course to preview, then Play",
    selectOnClick: true,
    back: "Back",
    onCancel: () => (mode === "split" ? openLobby(true) : mode === "online" ? openOnline() : openStartMenu()),
    onHighlight: (i) => courseSelect.highlight(i),
  });
  const courseSelect = new CourseSelect(selectMenu, scoreBoards, {
    play: (name) => {
      if (mode === "online" && !onlineHostReady()) return;
      // A course picked with a finger is the gesture a phone needs to go fullscreen (Android; an
      // iPhone plays fullscreen from the home screen instead).
      if ((touchMode || pendingTouchMode === true) && !isStandalone()) enterFullscreen();
      closeMenus();
      if (mode === "online") onlineCourse = name;
      loadCourse(name);
      // The host's pick starts the race for everyone in the room.
      if (mode === "online") session.announceCourse(name, courseKey(course));
    },
    getName: () => scores.name,
  });
  const settingsMenu = new ListMenu(document.body, {
    className: "settings-menu",
    title: "Settings",
    hint: "up and down to choose, left and right to change, Escape to go back",
    touchHint: "tap a setting to change it",
    back: "Back",
    onCancel: () => closeSettings(),
    onChange: () => settingsMenu.setItems(settingsItems(), settingsMenu.selected),
  });
  const pauseMenu = new ListMenu(document.body, {
    className: "pause-menu",
    title: "Paused",
    hint: "stick or arrows to choose, Jump or Enter to go, Start, Tuck, or Escape to resume",
    touchHint: "tap to choose",
    back: "Resume",
    onCancel: () => closeMenus(),
    onRestart: () => {
      closeMenus();
      restartAtStart();
    },
  });
  const burnoutSummary = document.createElement("div");
  burnoutSummary.className = "global-status burnout-summary";
  burnoutSummary.hidden = true;
  pauseMenu.content.appendChild(burnoutSummary);
  const resultsMenu = new ListMenu(document.body, {
    className: "results-menu",
    title: "Finish",
    hint: "Jump or Enter to choose, Restart to retry, Start or Escape for the course select",
    touchHint: "tap to choose",
    onCancel: () => openCourseSelect(),
    onRestart: () => {
      closeMenus();
      restartAtStart();
    },
  });
  const results = new ResultsScreen(resultsMenu, globalScores);
  const raceMenu = new ListMenu(document.body, {
    className: "results-menu race-menu",
    title: "Race",
    hint: "Jump or Enter to choose, Restart for a rematch, Start or Escape for the course select",
    touchHint: "tap to choose",
    back: "Courses",
    onCancel: () => openCourseSelect(),
    onRestart: () => {
      closeMenus();
      restartAtStart();
    },
  });
  const raceResults = new RaceResults(raceMenu);
  const leaderMenu = new ListMenu(document.body, {
    className: "leaderboard-menu",
    title: "Leaderboard",
    hint: "stick or arrows to choose, Jump or Enter for a course's runs, Escape to go back",
    touchHint: "tap a course for its runs",
    back: "Back",
    onCancel: () => openStartMenu(),
    onHighlight: (i) => leaderboard.highlight(i),
  });
  const leaderboard = new LeaderboardScreen(leaderMenu, { onCourse: (name) => openRuns(name) });
  const runsMenu = new ListMenu(document.body, {
    className: "runs-menu",
    title: "Runs",
    hint: "stick or arrows to choose, Jump or Enter to watch a run, Escape to go back",
    touchHint: "tap a run to watch it",
    back: "Back",
    onCancel: () => openLeaderboard(),
  });
  const runsScreen = new RunsScreen(runsMenu, { onReplay: (id, player) => startReplay(id, player) });
  /** The course whose runs list is open. */
  let runsCourse = courseName;
  const menus = [startMenu, selectMenu, settingsMenu, pauseMenu, resultsMenu, raceMenu, leaderMenu, runsMenu];
  // The title screens (the start menu, the join screen, the settings, the course select) sit on a
  // synthwave backdrop that hides the world; the pause menu and the results keep the game behind them.
  const backdrop = document.createElement("div");
  backdrop.className = "retro-backdrop";
  backdrop.hidden = true;
  // Two ninjas race an endless course across the sky (attract.ts); the grid rolls under its ground.
  const raceEl = document.createElement("div");
  raceEl.className = "retro-race";
  const grid = document.createElement("div");
  grid.className = "retro-grid";
  const gridLines = document.createElement("div");
  gridLines.className = "retro-grid-lines";
  grid.appendChild(gridLines);
  backdrop.append(grid, raceEl);
  document.body.appendChild(backdrop);
  const attract = new AttractRace(raceEl, tuning, feel, poses);
  const titleScreens = new Set<ListMenu | null>([
    startMenu,
    selectMenu,
    settingsMenu,
    leaderMenu,
    runsMenu,
    null,
  ]);
  /** Where the settings menu goes back to. */
  let settingsReturn: "start" | "lobby" = "start";

  const lobby = new Lobby(
    document.body,
    {
      roster,
      keyboard,
      tuning,
      feel,
      poses,
      onReady: (seats) => {
        raceSeats = lobbySeatsToRace(seats);
        mode = "split";
        openCourseSelect();
      },
      onSettings: () => openSettings("lobby"),
      onBack: () => openStartMenu(),
      onBand: (index, band) => {
        settings.bands[index] = band;
        if (index === 0) syncAppearanceBand(band);
        saveSettings(settings);
        refreshSeats();
      },
    },
    settings.bands,
  );

  // ---- The online race (`online/`, `docs/online.md`): a room over the relay (or between two tabs
  // with ?relay=local), the other players re-simulated here from their inputs and drawn as ghosts.
  const relayParam = params.get("relay");
  /** The course the online race is on, once the host picked one (null in the room before that). */
  let onlineCourse: string | null = null;
  const session = new OnlineSession(
    {
      name: () => scores.name,
      band: () => settings.bands[0] ?? 0,
      tuningHash: () => players[0]?.sim.tuning.hash ?? tuningHash(tuning),
      commit,
      tuning,
      course: () =>
        mode === "online" && onlineCourse !== null && players.length > 0
          ? { name: courseName, data: course }
          : null,
      localTape: () => players[0]?.recorder.snapshot()?.frames ?? null,
      peersChanged: () => {
        onlineScreen.render();
        syncOnlineGhosts();
        if (mode === "online" && players.length > 0) {
          refreshRunHud();
          checkFinished();
          if (raceMenu.open) raceResults.update(raceLines());
        }
      },
      stateChanged: () => {
        onlineScreen.render();
        if (mode === "online" && players.length > 0) refreshRunHud();
        // The initial welcome can arrive just after the player switches apps.
        if (document.hidden && (session.state === "hosting" || session.state === "joined")) session.suspend();
      },
      courseAnnounced: (name) => {
        if (!knownCourse(name) || soloOnly(name)) return;
        if (overlayOpen()) closeMenus();
        mode = "online";
        onlineCourse = name;
        loadCourse(name);
        showBanner(`${peerName(0)} picked ${course.title ?? name}`, 1500);
      },
      restartAnnounced: () => {
        if (mode !== "online") return;
        if (overlayOpen()) closeMenus();
        restartAtStart();
        showBanner("rematch", 1000);
      },
      remoteFinished: () => {
        if (mode !== "online") return;
        saveOnlineRun();
        checkFinished();
        if (raceMenu.open) raceResults.update(raceLines());
      },
      ended: (reason) => {
        const racing = mode === "online";
        leaveOnline();
        if (racing) showBanner(`race over: ${reason}`, 2500);
        openOnline();
        onlineScreen.say(`the race ended: ${reason}`, 6000);
      },
      // A ghost that drifts from its player's run (another build or tuning) is a note for the console,
      // not a banner over the race: the player's own run is never affected.
      warn: (text) => console.warn(`online: ${text}`),
    },
    relayParam && relayParam !== "local" ? relayParam : RELAY_URL,
    relayParam === "local",
  );
  const onlineScreen = new OnlineScreen(document.body, {
    session,
    getName: () => scores.name,
    setName: (name) => {
      scores.name = name;
      session.setName();
      refreshRunHud();
    },
    getBand: () => settings.bands[0] ?? 0,
    setBand: (band) => {
      settings.bands[0] = band;
      syncAppearanceBand(band);
      saveSettings(settings);
      refreshSeats();
      session.setBand(band);
    },
    onHost: () => {
      mode = "online";
      session.hostRoom();
    },
    onJoin: (code) => {
      mode = "online";
      session.join(code);
    },
    onLeave: () => {
      leaveOnline();
      openOnline();
    },
    onBack: () => {
      leaveOnline();
      openStartMenu();
    },
    onPick: () => {
      if (session.isHost) openCourseSelect();
    },
    present: (menu) => show(menu, () => menu.show(0)),
  });
  menus.push(onlineScreen.menu, onlineScreen.room);
  titleScreens.add(onlineScreen.menu);
  titleScreens.add(onlineScreen.room);

  // Phones may freeze JavaScript while leaving a socket apparently open. Release the connection
  // before suspension so the room can mark this seat away, then reclaim it on return.
  function suspendPage(): void {
    keyboard.cancelAll();
    cancelTouchGestures();
    session.suspend();
    if (touchMode && !overlayOpen() && players.length > 0) openPause();
  }
  function resumePage(): void {
    if (!document.hidden) session.resume();
  }
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) suspendPage();
    else resumePage();
  });
  window.addEventListener("pagehide", suspendPage);
  window.addEventListener("pageshow", resumePage);
  window.addEventListener("online", resumePage);

  function onlineHostReady(): boolean {
    if (session.state === "hosting") return true;
    if (session.isHost) showBanner("Reconnecting…", 1500);
    return false;
  }

  const usernameScreen = new UsernameScreen(document.body, {
    getName: () => scores.name,
    hasName: () => scores.hasName,
    setName: (name) => {
      scores.confirmName(name);
      session.setName();
      if (courseName === BURNOUT_COURSE && burnoutProgress.ticks === 0) restartAtStart();
      refreshRunHud();
    },
    present: (menu, open) => show(menu, open),
  });
  menus.push(usernameScreen.menu);
  titleScreens.add(usernameScreen.menu);
  const globalLeaderboard = new GlobalLeaderboardScreen(
    document.body,
    scoreBoards,
    [
      ...timedCourseNames.map((name) => ({ name, title: courseData(name).title ?? name })),
      { name: BURNOUT_COURSE, title: BURNOUT_TITLE },
    ],
    { onBack: openStartMenu, onLocal: openLeaderboard },
  );
  menus.push(globalLeaderboard.menu);
  titleScreens.add(globalLeaderboard.menu);
  const globalResultNotes = [resultsMenu, raceMenu].map((menu) => {
    const note = document.createElement("div");
    note.className = "global-status score-sync";
    note.setAttribute("role", "status");
    menu.content.appendChild(note);
    return note;
  });

  function openOnline(): void {
    if (soloOnly(courseName)) loadCourse(GYM);
    onlineScreen.show();
  }

  /** Out of the room: the ghosts go, the next game is a time trial. */
  function leaveOnline(): void {
    session.leave();
    onlineCourse = null;
    if (mode === "online") mode = "single";
    for (const p of players)
      for (const key of [...p.ghosts.keys()]) if (key.startsWith("n")) p.removeGhost(key);
    if (players.length > 0) {
      buildPlayers(currentSeats());
      refreshRunHud();
    }
  }

  /** One ghost per remote player in the local viewport, in their headband color, shown by the ghost setting. */
  function syncOnlineGhosts(): void {
    const first = players[0];
    if (!first) return;
    const keys = new Set<string>();
    if (mode === "online") {
      for (const [peer, remote] of session.remotes) {
        const key = `n${peer}`;
        keys.add(key);
        const p = session.peers.get(peer);
        first.addGhostSource(key, remote, headband(p?.band ?? 0).color, "run");
        first.setGhostActive(key, settings.ghost && !p?.left && !p?.away);
      }
    }
    first.ghosts.forEach((_, key) => {
      if (key.startsWith("n") && !keys.has(key)) first.removeGhost(key);
    });
  }

  /** The online race's record: the local run and every remote run as they stand, saved at each finish. */
  function saveOnlineRun(): void {
    const first = players[0];
    if (!first || mode !== "online") return;
    if (!raceRecord) {
      raceRecord = {
        id: newRunId(),
        course: courseName,
        key: courseKey(course),
        date: Date.now(),
        tuning: first.sim.tuning.hash,
        mode: "race",
        players: [],
      };
    }
    const done = first.finish;
    raceRecord.players = [
      runPlayerOf(first, done ? done.time : null, done ? done.falls : first.sim.state.runFalls),
    ];
    for (const [peer, remote] of session.remotes) {
      const info = session.peers.get(peer);
      const tape = remote.recorder.snapshot() ?? {
        version: 0,
        course: courseName,
        tuningHash: first.sim.tuning.hash,
        seed: 0,
        frames: [],
      };
      raceRecord.players.push({
        name: info?.name ?? `Player ${peer + 1}`,
        band: info?.band ?? 0,
        time: remote.finish ? remote.finish.time : null,
        falls: remote.finish ? remote.finish.falls : remote.sim.state.runFalls,
        startTick: remote.runStartTick,
        tape,
      });
    }
    raceRecord.date = Date.now();
    runs.put(raceRecord);
    lastRunId = raceRecord.id;
  }

  // Controls stay available in Pause; the title screen's right column shows recent finishes.
  function touchTips(): HTMLDivElement {
    const tips = document.createElement("div");
    tips.className = "touch-tips";
    const help = document.createElement("div");
    help.textContent = TOUCH_HELP;
    tips.appendChild(help);
    return tips;
  }
  const pauseTips = touchTips();
  pauseMenu.content.appendChild(pauseTips);
  const installScreen = new InstallScreen(document.body, installation, show);
  menus.push(installScreen.menu);
  titleScreens.add(installScreen.menu);
  // Keep Install reachable without scrolling the main menu, and available from Pause too.
  const startInstall = document.createElement("button");
  startInstall.type = "button";
  startInstall.className = "menu-back start-install";
  startInstall.textContent = "Install Lache";
  startMenu.el.appendChild(startInstall);
  const installButton = document.createElement("button");
  installButton.type = "button";
  installButton.className = "tip-install";
  installButton.textContent = "Install Lache";
  pauseTips.appendChild(installButton);
  for (const [button, back] of [
    [startInstall, openStartMenu],
    [installButton, openPause],
  ] as const) {
    button.addEventListener("click", (event) => {
      if (!event.defaultPrevented) installScreen.open(back);
    });
    button.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      event.stopPropagation();
      button.click();
    });
    button.addEventListener("keyup", (event) => {
      if (event.key === "Enter" || event.key === " ") event.stopPropagation();
    });
  }
  function updateInstallButtons(): void {
    const installed = installation.installed;
    startInstall.hidden = installed;
    installButton.hidden = installed;
    installButton.classList.toggle("available", !installed);
    startInstall.disabled = installation.busy;
    installButton.disabled = installation.busy;
    rootClasses.toggle("standalone", isStandalone());
  }
  installation.subscribe(updateInstallButtons);
  updateInstallButtons();

  function openMenu(): ListMenu | null {
    return menus.find((m) => m.open) ?? null;
  }

  /** A menu or the join screen is up: the simulation waits and the touch controls hide. */
  function overlayOpen(): boolean {
    return characterScreen.open || lobby.open || openMenu() !== null;
  }

  function closeMenus(): void {
    recentTimes.setVisible(false);
    characterScreen.hide();
    for (const m of menus) m.hide();
    lobby.hide();
    backdrop.hidden = true;
    loop.paused = false;
    captureClosingInputs();
    applyTouchUi();
  }

  /** Suppress only the actions held at the handoff, never a fresh press before the next frame. */
  function captureClosingInputs(): void {
    const now = performance.now();
    for (const p of players) {
      p.cond.onBar = p.sim.state.barId >= 0 ? 1 : 0;
      p.cond.inAir =
        p.sim.state.grounded === 0 && p.sim.state.barId < 0 && p.sim.state.ledgeSolid < 0 ? 1 : 0;
      p.input.sample(p.liveInput, p.cond, now);
      p.gate.arm(p.liveInput);
      p.prevPause = p.liveInput.pause;
    }
  }

  function show(menu: ListMenu | null, open: () => void): void {
    characterScreen.hide();
    for (const m of menus) if (m !== menu) m.hide();
    if (menu !== null) lobby.hide();
    backdrop.hidden = !titleScreens.has(menu);
    // An online race never pauses: the others race on, and their ghosts keep arriving.
    loop.paused = !(mode === "online" && session.active);
    open();
    const opened = openMenu();
    if (opened) {
      const now = performance.now();
      for (const p of players) p.input.sample(p.liveInput, p.cond, now);
      mergeFrames(menuInput, players);
      opened.armInput(menuInput, now);
    }
    recentTimes.setVisible(startMenu.open);
    applyTouchUi();
  }

  function nextCourseName(): string | null {
    const i = timedCourseNames.indexOf(courseName);
    return i >= 0 && i + 1 < timedCourseNames.length ? timedCourseNames[i + 1]! : null;
  }

  function openStartMenu(): void {
    const items: MenuItem[] = [
      {
        label: "Single player",
        action: () => {
          if (mode === "replay") leaveReplay("single");
          mode = "single";
          openCourseSelect();
        },
      },
      ...(mobileDevice ? [] : [{ label: "Split-screen", action: () => openLobby() }]),
      { label: "Multiplayer", action: () => openOnline() },
      {
        label: "Leaderboard",
        action: () => (globalScores.enabled ? openGlobalLeaderboard() : openLeaderboard()),
      },
      { label: "Character", action: () => openCharacter() },
      { label: "Settings", action: () => openSettings("start") },
    ];
    const selected = mode === "split" && !mobileDevice ? 1 : 0;
    show(startMenu, () => {
      startMenu.setItems(items, selected);
      startMenu.show(selected);
      // The screen switches on like a tube each time the title comes up.
      startMenu.el.classList.remove("boot");
      void startMenu.el.offsetWidth;
      startMenu.el.classList.add("boot");
    });
  }

  function openCharacter(): void {
    keyboard.cancelAll();
    cancelTouchGestures();
    show(null, () => {
      lobby.hide();
      backdrop.hidden = true;
      characterScreen.show(
        appearance ?? {
          ...DEFAULT_APPEARANCE,
          hair: "none",
          headbandColor: cssColor(headband(settings.bands[0] ?? 0).color),
        },
      );
    });
  }

  function openLobby(keepReady = false): void {
    show(null, () => {
      for (const m of menus) m.hide();
      for (const [index, seat] of lobby.seats.entries()) seat.band = settings.bands[index] ?? seat.band;
      lobby.show(keepReady);
    });
  }

  /** The course's picture on the course select, captured by `e2e/thumbs.ts` into `public/thumbs`. */
  function courseThumb(name: string): string {
    return `/thumbs/${name}.png`;
  }

  function openCourseSelect(): void {
    if (mode === "online" && !onlineHostReady()) return;
    // Leaving a replay for a course lands in a time trial.
    if (mode === "replay") leaveReplay("single");
    const choices: CourseChoice[] = [];
    if (mode === "single")
      choices.push({
        name: LACHE_PRACTICE,
        title: "Lache Practice",
        board: [],
        note: "swings and links",
        image: courseThumb(LACHE_PRACTICE),
      });
    choices.push({
      name: GYM,
      title: "Gym",
      board: [],
      note: "free practice",
      image: courseThumb(GYM),
      separatorAfter: true,
    });
    choices.push(
      ...timedCourseNames.map((name, index) => {
        const data = courseData(name);
        return {
          name,
          title: data.title ?? name,
          stage: index + 1,
          board: scores.board(courseKey(data)),
          image: courseThumb(name),
        };
      }),
    );
    if (mode === "single")
      choices.push({
        name: BURNOUT_COURSE,
        title: BURNOUT_TITLE,
        board: burnoutBoard(),
        endurance: true,
        image: courseThumb(BURNOUT_COURSE),
      });
    if (mode === "single")
      choices.push({
        name: INFINITE_COURSE,
        title: INFINITE_TITLE,
        board: [],
        note: "endless practice",
        image: courseThumb(INFINITE_COURSE),
      });
    selectMenu.setTitle(
      mode === "split"
        ? "Courses · split-screen race"
        : mode === "online"
          ? "Courses · multiplayer"
          : "Courses",
    );
    show(selectMenu, () => courseSelect.open(choices, courseName));
  }

  /**
   * The settings rows. Left and right (or Jump) step a row; the menu rebuilds itself after each
   * step. A new setting is one more row here and one field in `GameSettings`.
   */
  function settingsItems(): MenuItem[] {
    return [
      {
        label: "Ghost",
        detail: settings.ghost ? "on" : "off",
        help: "The other players show as ghosts where they are, in a race.",
        adjust: () => {
          settings.ghost = !settings.ghost;
          saveSettings(settings);
          syncOnlineGhosts();
        },
      },
      {
        label: "Best-time ghost",
        detail: settings.bestGhost ? "on" : "off",
        help: "Your fastest saved run races beside you in a time trial.",
        adjust: () => {
          settings.bestGhost = !settings.bestGhost;
          saveSettings(settings);
          if (!settings.bestGhost) armBestGhost(false);
        },
      },
      {
        label: "Replay cams",
        detail: settings.replayAllCams ? "every player" : "picked player",
        help: "A race replay shows every player's cam, or only the picked player's.",
        adjust: () => {
          settings.replayAllCams = !settings.replayAllCams;
          saveSettings(settings);
        },
      },
      {
        label: "CRT scanlines",
        detail: settings.crt ? "on" : "off",
        help: "A tube over the game: scanlines and dark corners.",
        adjust: () => {
          settings.crt = !settings.crt;
          saveSettings(settings);
          applyCrt();
        },
      },
      {
        label: "Music",
        detail: settings.musicLevel > 0 ? (MUSIC_LEVEL_NAMES[settings.musicLevel] ?? "on") : "off",
        help: "Music and PERFECT boost sounds, and how loud.",
        adjust: (dir) => {
          const n = MUSIC_LEVELS.length;
          settings.musicLevel = (((settings.musicLevel + dir) % n) + n) % n;
          saveSettings(settings);
          music.setLevel(settings.musicLevel);
        },
      },
      {
        label: "Headband",
        detail: headband(settings.bands[0] ?? 0).name,
        help: "Your headband, sash, and the ghost the other players see.",
        adjust: (dir) => {
          settings.bands[0] = ((settings.bands[0] ?? 0) + dir + HEADBANDS.length) % HEADBANDS.length;
          syncAppearanceBand(settings.bands[0]);
          saveSettings(settings);
          refreshSeats();
          session.setBand(settings.bands[0]);
        },
      },
      {
        label: "Username",
        detail: scores.name,
        help: "Your name on the leaderboard and in multiplayer races.",
        action: () => usernameScreen.open(false, () => openSettings(settingsReturn)),
      },
      {
        label: "Touch lache help",
        detail: settings.touchLacheHelp ? "on" : "off",
        help: "Slide GRAB to RELEASE without lifting. Slide back to GRAB during flight for the next link. Tuck is manual.",
        adjust: () => {
          settings.touchLacheHelp = !settings.touchLacheHelp;
          touchReader.config.lacheAssist = settings.touchLacheHelp;
          saveSettings(settings);
        },
      },
      {
        label: "Gym tutorial",
        detail: settings.gymTutorial ? "on" : "off",
        help: "Instructions and feedback while you explore the Gym.",
        adjust: () => toggleGymTutorial(),
      },
      { label: "Back", detail: touchMode ? "" : "Esc", action: () => closeSettings() },
    ];
  }

  function openSettings(returnTo: "start" | "lobby"): void {
    settingsReturn = returnTo;
    show(settingsMenu, () => {
      settingsMenu.setItems(settingsItems(), 0);
      settingsMenu.show(0);
    });
  }

  function closeSettings(): void {
    if (settingsReturn === "lobby") openLobby(true);
    else openStartMenu();
  }

  function openPause(): void {
    const first = players[0];
    if (!first) return;
    const timed = first.sim.world.timed;
    burnoutSummary.hidden = courseName !== BURNOUT_COURSE;
    if (mode === "replay" && replayOf) {
      const record = replayOf.record;
      pauseMenu.setTitle(`${first.sim.world.title} · replay paused`);
      show(pauseMenu, () => {
        pauseMenu.setItems(
          [
            { label: "Resume", action: () => closeMenus() },
            { label: "Restart replay", detail: touchMode ? "" : "R", action: () => restartAtStart() },
            { label: "Stop replay", detail: "", action: () => openRuns(record.course) },
            { label: "Main menu", detail: "", action: () => openStartMenu() },
          ],
          0,
        );
        pauseMenu.show(0);
      });
      return;
    }
    const items: MenuItem[] = [
      { label: "Resume", action: () => closeMenus() },
      {
        label: soloPractice(courseName) ? "Retry checkpoint" : timed ? "Restart" : "Restart at the start",
        detail: touchMode ? "" : "R",
        action: () => {
          closeMenus();
          restartAtStart(soloPractice(courseName) ? first.sim.state.respawnIndex : 0);
        },
      },
    ];
    if (courseName === GYM && mode === "single") {
      const tutorialItem: MenuItem = {
        label: "Gym tutorial",
        detail: settings.gymTutorial ? "on" : "off",
        adjust: () => {
          toggleGymTutorial();
          tutorialItem.detail = settings.gymTutorial ? "on" : "off";
          pauseMenu.setItems(items, pauseMenu.selected);
        },
      };
      items.push(tutorialItem);
    }
    if (courseName === INFINITE_COURSE)
      items.push({
        label: "New course",
        action: () => {
          closeMenus();
          practiceSeed = newPracticeSeed();
          practiceWindow = null;
          loadCourse(INFINITE_COURSE);
        },
      });
    // Authored practice courses offer direct jumps to their sections.
    // In a race, a player who cannot or will not finish ends it for everyone from here.
    if (mode === "split")
      items.push({ label: "Forfeit", detail: "end the race now", action: () => forfeitRace() });
    // Online the others race on: leaving is only leaving.
    if (mode === "online") {
      if (session.isHost)
        items.push({
          label: "Course select",
          detail: "pick another course for everyone",
          action: () => openCourseSelect(),
        });
      items.push({
        label: "Leave the race",
        detail: session.isHost ? "end the room" : "the others race on",
        action: () => {
          leaveOnline();
          openOnline();
        },
      });
      items.push({
        label: "Main menu",
        detail: "",
        action: () => {
          leaveOnline();
          openStartMenu();
        },
      });
      pauseMenu.setTitle(`${timed ? first.sim.world.title : "Gym"} · multiplayer`);
      show(pauseMenu, () => {
        pauseMenu.setItems(items, 0);
        pauseMenu.show(0);
      });
      return;
    }
    if (!timed && !streamedCourse(courseName)) {
      first.sim.world.respawnNames.forEach((name, index) => {
        if (index === 0 && courseName !== LACHE_PRACTICE) return;
        items.push({
          label: name || `section ${index}`,
          detail: "",
          action: () => {
            closeMenus();
            restartAtStart(index);
            showBanner(name || `section ${index}`, 900);
          },
        });
      });
    }
    items.push({ label: "Course select", detail: "", action: () => openCourseSelect() });
    items.push({ label: "Main menu", detail: "", action: () => openStartMenu() });
    const title = first.sim.world.title;
    pauseMenu.setTitle(mode === "split" ? `${title} · race paused` : `${title} · paused`);
    show(pauseMenu, () => {
      pauseMenu.setItems(items, 0);
      pauseMenu.show(0);
    });
  }

  function afterFinishItems(): MenuItem[] {
    if (mode === "online") return afterOnlineFinishItems();
    const items: MenuItem[] = [
      {
        label: mode === "split" ? "Rematch" : "Retry",
        detail: touchMode ? "" : "R",
        action: () => {
          closeMenus();
          restartAtStart();
        },
      },
    ];
    const saved = lastRunId;
    if (saved) items.push({ label: "Watch replay", detail: "", action: () => startReplay(saved, 0) });
    const next = nextCourseName();
    if (next) {
      const title = courseData(next).title ?? next;
      items.push({
        label: `Next: ${title}`,
        detail: "",
        action: () => {
          closeMenus();
          loadCourse(next);
        },
      });
    }
    items.push({ label: "Course select", detail: touchMode ? "" : "Esc", action: () => openCourseSelect() });
    items.push({ label: "Main menu", action: () => openStartMenu() });
    return items;
  }

  /** After an online race: the host restarts or moves everyone on, a guest can only watch or leave. */
  function afterOnlineFinishItems(): MenuItem[] {
    const items: MenuItem[] = [];
    if (session.isHost) {
      items.push({
        label: "Rematch",
        detail: touchMode ? "everyone" : "R · everyone",
        action: () => {
          if (!onlineHostReady()) return;
          closeMenus();
          restartAtStart();
          session.announceRestart();
        },
      });
    } else {
      items.push({
        label: "Run it again",
        detail: touchMode ? "" : "R",
        action: () => {
          closeMenus();
          restartAtStart();
        },
      });
    }
    const saved = lastRunId;
    if (saved) items.push({ label: "Watch replay", detail: "", action: () => startReplay(saved, 0) });
    if (session.isHost) {
      const next = nextCourseName();
      if (next) {
        const title = courseData(next).title ?? next;
        items.push({
          label: `Next: ${title}`,
          detail: "everyone",
          action: () => {
            if (!onlineHostReady()) return;
            closeMenus();
            onlineCourse = next;
            loadCourse(next);
            session.announceCourse(next, courseKey(course));
          },
        });
      }
      items.push({ label: "Course select", detail: "everyone", action: () => openCourseSelect() });
    }
    items.push({
      label: "Leave the race",
      detail: "",
      action: () => {
        leaveOnline();
        openOnline();
      },
    });
    return items;
  }

  function openResults(placement: Placement): void {
    const first = players[0];
    if (!first) return;
    show(resultsMenu, () => results.show(courseName, first.sim.world.title, placement, afterFinishItems()));
  }

  function raceLines(): RaceLine[] {
    const lines: RaceLine[] = players.map((p) => ({
      tag: `P${(mode === "online" ? session.myPeer : p.index) + 1}`,
      name: playerName(p),
      color: cssColor(headband(p.seat.band).color),
      time: p.finish ? p.finish.time : null,
      falls: p.finish ? p.finish.falls : p.sim.state.runFalls,
    }));
    if (mode === "online") {
      for (const p of session.others()) {
        const remote = session.remotes.get(p.peer);
        lines.push({
          tag: `P${p.peer + 1}`,
          name: p.left ? `${p.name} (left)` : p.away ? `${p.name} (away)` : p.name,
          color: cssColor(headband(p.band).color),
          time: remote?.finish ? remote.finish.time : null,
          falls: remote?.finish ? remote.finish.falls : (remote?.sim.state.runFalls ?? 0),
        });
      }
    }
    return lines;
  }

  function openRaceResults(): void {
    const first = players[0];
    if (!first) return;
    show(raceMenu, () => raceResults.show(first.sim.world.title, raceLines(), afterFinishItems()));
  }

  // ---- The leaderboard, the runs list, replays, and the best-time ghost (`docs/replays.md`)

  function openGlobalLeaderboard(): void {
    show(globalLeaderboard.menu, () => globalLeaderboard.open(runsCourse));
  }

  function openLeaderboard(): void {
    const choices: LeaderboardChoice[] = timedCourseNames.map((name) => {
      const data = courseData(name);
      const key = courseKey(data);
      return { name, title: data.title ?? name, board: scores.board(key), runs: runs.list(key).length };
    });
    choices.push({ name: BURNOUT_COURSE, title: BURNOUT_TITLE, board: burnoutBoard(), runs: 0 });
    show(leaderMenu, () => leaderboard.open(choices, runsCourse));
  }

  function openRuns(name: string): void {
    if (name === BURNOUT_COURSE) {
      runsCourse = name;
      openLeaderboard();
      return;
    }
    if (!timedCourseNames.includes(name)) return;
    runsCourse = name;
    const data = courseData(name);
    const key = courseKey(data);
    show(runsMenu, () =>
      runsScreen.show(data.title ?? name, scores.board(key), runRows(runs.list(key)), settings.replayAllCams),
    );
  }

  /** Every player's tape and time so far, for a run record. */
  function runPlayerOf(p: Player, time: number | null, falls: number): RunPlayer {
    const tape = p.recorder.snapshot() ?? {
      version: 0,
      course: courseName,
      tuningHash: p.sim.tuning.hash,
      seed: 0,
      frames: [],
    };
    return { name: playerName(p), band: p.seat.band, time, falls, startTick: p.runStartTick, tape };
  }

  /** A finish: keep the run with its inputs (a race's record fills in as each player finishes). */
  function saveRun(p: Player, time: number, falls: number): void {
    const key = courseKey(course);
    if (mode === "online") {
      saveOnlineRun();
      return;
    }
    if (mode === "split") {
      if (!raceRecord) {
        raceRecord = {
          id: newRunId(),
          course: courseName,
          key,
          date: Date.now(),
          tuning: p.sim.tuning.hash,
          mode: "race",
          players: players.map((q) => runPlayerOf(q, null, q.sim.state.runFalls)),
        };
      }
      // This player's finish, and the others' tapes as they stand.
      for (const q of players) {
        const done = q.finish;
        raceRecord.players[q.index] = runPlayerOf(
          q,
          done ? done.time : null,
          done ? done.falls : q.sim.state.runFalls,
        );
      }
      raceRecord.date = Date.now();
      runs.put(raceRecord);
      lastRunId = raceRecord.id;
      return;
    }
    const record: RunRecord = {
      id: newRunId(),
      course: courseName,
      key,
      date: Date.now(),
      tuning: p.sim.tuning.hash,
      mode: "single",
      players: [runPlayerOf(p, time, falls)],
    };
    runs.put(record);
    lastRunId = record.id;
    loadBestGhost();
  }

  /** The fastest saved run under the player's name on this course becomes the best-time ghost. */
  function loadBestGhost(): void {
    const best = runs.best(courseKey(course), scores.name);
    const rp = best ? best.record.players[best.player] : undefined;
    bestGhost.load(rp ? rp.tape : null, rp ? rp.startTick : 0, rp ? rp.time : null);
    syncBestGhostLook();
    armBestGhost(false);
  }

  function syncBestGhostLook(): void {
    const best = runs.best(courseKey(course), scores.name);
    const band = best ? (best.record.players[best.player]?.band ?? 0) : 0;
    players[0]?.setGhostAccent("best", headband(band).color);
  }

  /** Start the best-time ghost at the player's start line crossing, or put it away. */
  function armBestGhost(on: boolean): void {
    const first = players[0];
    if (on && mode === "single" && settings.bestGhost && bestGhost.loaded && first) {
      bestGhost.arm();
      first.setGhostActive("best", true);
      return;
    }
    bestGhost.disarm();
    first?.setGhostActive("best", false);
  }

  /** Replay a saved run: every cam, or the picked player's cam with the rest as ghosts. */
  function startReplay(id: string, player: number): void {
    const record = runs.get(id);
    if (!record || !knownCourse(record.course)) return;
    // Retain retired runs in storage without replaying their inputs on a different layout.
    if (record.key !== courseKey(courseData(record.course))) return;
    if (overlayOpen()) closeMenus();
    const all = settings.replayAllCams && record.players.length > 1;
    const picked = Math.max(0, Math.min(player, record.players.length - 1));
    const shown = all ? record.players.map((_, i) => i) : [picked];
    replayPlayers = shown.map((i) => record.players[i]!);
    replaySeats = replayPlayers.map((rp, i) => ({
      pad: -1,
      slot: -1,
      keyboard: false,
      band: rp.band,
      scheme: seatScheme(i),
    }));
    mode = "replay";
    replayOf = { record, player: picked };
    replayEndAt = -1;
    replayStarting = true;
    loadCourse(record.course, false);
    replayStarting = false;
    for (const g of replayGhosts) g.disarm();
    players[0]?.ghosts.forEach((_, key) => {
      if (key.startsWith("r")) players[0]?.removeGhost(key);
    });
    replayGhosts = [];
    // The tapes drive the players; the skipped warm-up ends a second before the earliest start line crossing.
    const skip = Math.max(0, Math.min(...replayPlayers.map((rp) => rp.startTick)) - HZ);
    players.forEach((p, i) => {
      const rp = replayPlayers[i]!;
      p.setTuning(replayTuning(rp.tape.tuningHash, tuning) ?? tuning);
      p.recorder.play(rp.tape);
      p.setTag(all ? `P${shown[i]! + 1}` : null);
      p.hud.setSub(`replay  ·  ${formatWhen(record.date)}  ·  ${rp.name}`);
    });
    if (!all) {
      // The players not shown as cams run beside the cam as ghosts (the ghost setting shows them).
      const cam = players[0]!;
      record.players.forEach((rp, i) => {
        if (i === picked) return;
        const ghost = new BestGhost(course, tuning);
        ghost.load(rp.tape, 0, rp.time);
        ghost.arm();
        replayGhosts.push(ghost);
        cam.addGhostSource(`r${i}`, ghost, headband(rp.band).color, "run");
        cam.setGhostActive(`r${i}`, settings.ghost);
      });
    }
    for (let k = 0; k < skip; k++) stepAll();
    for (const p of players) p.viewport.camera.snapTo(p.sim.state.x, p.sim.state.y + 0.9);
    if (record.players.some((rp) => replayTuning(rp.tape.tuningHash, tuning) === null))
      showBanner("this run was made with another tuning: the replay may differ", 3000);
    else showBanner(`replay  ·  ${formatWhen(record.date)}`, 1500);
  }

  /** Out of a replay: its ghosts go, and the next game is a time trial or a race. */
  function leaveReplay(next: "single" | "split"): void {
    for (const g of replayGhosts) g.disarm();
    players[0]?.ghosts.forEach((_, key) => {
      if (key.startsWith("r")) players[0]?.removeGhost(key);
    });
    replayGhosts = [];
    replayOf = null;
    replayPlayers = [];
    replayEndAt = -1;
    for (const p of players) {
      p.recorder.stopPlayback();
      p.setTuning(tuning);
    }
    mode = next;
  }

  /** A player's tape ran out: a test's playback resolves; a replay returns to the list once every tape has. */
  function onTapeEnd(p: Player): void {
    if (p.index === 0 && playbackResolve) finishPlayback();
    if (mode === "replay" && players.every((q) => !q.recorder.playing)) {
      replayEndAt = players[0]!.sim.state.tick + RESULTS_DELAY_TICKS;
    }
  }

  // ---- Courses and runs

  function refreshRunHud(): void {
    for (const p of players) {
      if (courseName === BURNOUT_COURSE) {
        p.hud.setCourse(BURNOUT_TITLE);
        p.hud.setBurnout(burnoutProgress.ticks / HZ, burnoutProgress.obstacles);
        continue;
      }
      if (courseName === INFINITE_COURSE && practiceWindow) {
        p.hud.setCourse(INFINITE_TITLE);
        p.hud.setPractice(
          practiceDistance,
          Math.max(1, Math.floor((practiceWindow.originX + p.sim.state.x) / INFINITE_CHUNK_LENGTH) + 1),
        );
        continue;
      }
      if (!p.sim.world.timed) {
        p.hud.setMode(ClockMode.Hidden);
        continue;
      }
      const key = courseKey(course);
      p.hud.setCourse(p.sim.world.title);
      if (mode === "replay") {
        p.bestSplits = [];
        p.hud.setSub("replay");
        continue;
      }
      if (mode === "split") {
        // A race compares the players with each other at the checkpoints, not with the board.
        p.bestSplits = [];
        p.hud.setSub(`${playerName(p)}  ·  ${deviceLabel(p.seat)}`);
        continue;
      }
      if (mode === "online") {
        p.bestSplits = [];
        const others = session.present().map((q) => q.name);
        p.hud.setSub(
          session.state === "reconnecting"
            ? `${playerName(p)}  ·  reconnecting…`
            : others.length > 0
              ? `${playerName(p)}  ·  multiplayer  ·  vs ${nameList(others)}`
              : `${playerName(p)}  ·  multiplayer  ·  waiting for others`,
        );
        continue;
      }
      const mine = scores.personalBest(key, scores.name);
      const record = scores.record(key);
      p.bestSplits = mine?.splits ?? [];
      const parts: string[] = [];
      parts.push(mine ? `best ${formatTime(mine.time)}` : "no best yet");
      if (record && record !== mine) parts.push(`record ${formatTime(record.time)} ${record.name}`);
      p.hud.setSub(parts.join("  ·  "));
    }
  }

  /** Switch to course `name` (rebuilding the simulations and their drawing when it changes) and restart it. */
  function loadCourse(name: string, writeUrl = true): void {
    // Dedicated practice courses stay solo; the shared gym remains available in race modes.
    if (soloOnly(name) && mode !== "single") name = GYM;
    // The players follow the mode (one seat, or the race's seats); unchanged seats keep their players.
    buildPlayers(currentSeats());
    if (courseName === BURNOUT_COURSE && name !== BURNOUT_COURSE) {
      burnoutScores.abandon();
      burnoutWindow = null;
    }
    if (name === BURNOUT_COURSE) {
      practiceWindow = null;
      burnoutWindow = generateBurnoutWindow();
      courseName = name;
      course = burnoutWindow.course;
      bestGhost.setCourse(course, tuning);
    } else if (name === INFINITE_COURSE) {
      if (!practiceWindow) practiceWindow = generateInfiniteWindow(practiceSeed);
      else if (writeUrl) {
        practiceSeed = newPracticeSeed();
        practiceWindow = generateInfiniteWindow(practiceSeed);
      }
      practiceDistance = 0;
      courseName = name;
      course = practiceWindow.course;
      for (const p of players) p.setCourse(course, tuning, feel);
      bestGhost.setCourse(course, tuning);
      applyRendererMode();
    } else if (name !== courseName || players.some((p) => p.sim.world.title !== course.title)) {
      practiceWindow = null;
      courseName = name;
      course = courseData(name);
      for (const p of players) p.setCourse(course, tuning, feel);
      bestGhost.setCourse(course, tuning);
      applyRendererMode();
    }
    loadBestGhost();
    restartAtStart();
    if (mode === "online") syncOnlineGhosts();
    for (const p of players) p.viewport.camera.snapTo(p.sim.state.x, p.sim.state.y + 0.9);
    applyDevOverlays();
    const first = players[0]!;
    document.title = `Lache · ${first.sim.world.title}`;
    if (!writeUrl && name !== INFINITE_COURSE) return;
    try {
      // A reload (the commit plugin reloads after a commit) comes back to the same course and mode.
      const url = new URL(location.href);
      url.searchParams.set("course", name);
      if (name === INFINITE_COURSE) url.searchParams.set("practiceSeed", String(practiceSeed));
      else url.searchParams.delete("practiceSeed");
      if (mode === "split") url.searchParams.set("players", String(players.length));
      else url.searchParams.delete("players");
      if (writeUrl && mode !== "online") url.searchParams.delete("room");
      history.replaceState(null, "", url);
    } catch {
      // Not critical: a reload opens the start menu instead.
    }
  }

  function restartAtStart(respawn = 0): void {
    // A restart during a replay plays the run again from its start.
    if (mode === "replay" && replayOf && !replayStarting) {
      startReplay(replayOf.record.id, replayOf.player);
      return;
    }
    if (courseName === BURNOUT_COURSE) {
      burnoutScores.abandon();
      burnoutWindow = generateBurnoutWindow();
      burnoutProgress = createBurnoutProgress();
      course = burnoutWindow.course;
      for (const p of players) p.setCourse(course, tuning, feel);
      respawn = 0;
    }
    for (const p of players) p.restart(respawn);
    gymTutorial?.resetAttempt();
    if (courseName !== GYM) gymTutorial = null;
    if (streamedCourse(courseName))
      for (const p of players) {
        p.recorder.discard();
        p.recorder.stopPlayback();
      }
    if (courseName === BURNOUT_COURSE) {
      if (!useSaved || !scores.needsNamePrompt) burnoutScores.begin(scores.name, players[0]!.sim.tuning.hash);
      for (const p of players) p.viewport.camera.snapTo(p.sim.state.x, p.sim.state.y + 0.9);
      applyRendererMode();
    }
    scoreGeneration++;
    for (const note of globalResultNotes) note.textContent = "";
    resultsAtTick = -1;
    raceRecord = null;
    armBestGhost(false);
    // An online run starting over is a new generation of the input stream, and fresh replicas.
    if (mode === "online") session.localRestart();
    refreshRunHud();
  }

  function recordFinish(p: Player, time: number, falls: number, splits: number[]): void {
    const entry = {
      name: playerName(p),
      time,
      falls,
      date: Date.now(),
      tuning: p.sim.tuning.hash,
      splits,
    };
    lastPlacement = scores.add(courseKey(course), entry);
    if (globalScores.enabled) {
      const tape = p.recorder.snapshot();
      if (tape) {
        const generation = scoreGeneration;
        for (const note of globalResultNotes) note.textContent = "Saving global score…";
        void globalScores.submit(courseName, entry.name, tape, p.index, entry.date).then((result) => {
          if (generation !== scoreGeneration) return;
          const message =
            result === "accepted"
              ? "Saved to the global leaderboard."
              : result === "queued"
                ? "Saved on this device. Global score waiting to sync."
                : "Saved on this device. This run could not enter the global leaderboard.";
          for (const note of globalResultNotes) note.textContent = message;
        });
      }
    }
    refreshRunHud();
  }

  /**
   * The results open after a short pause once the run is over: in a time trial at the finish, in a
   * race when every player has finished (the owner's default, 2026-09-20: the others get to finish),
   * or at the first finish when the race-end setting says so.
   */
  function checkFinished(): void {
    if (mode === "online") {
      const first = players[0];
      if (!first) return;
      const present = session.present();
      const finished =
        (first.finish ? 1 : 0) + present.filter((p) => session.remotes.get(p.peer)?.finish).length;
      const over = finished === present.length + 1 || (settings.raceEndsFirst && finished > 0);
      if (over) {
        if (resultsAtTick < 0 && !raceMenu.open) resultsAtTick = first.sim.state.tick + RESULTS_DELAY_TICKS;
      } else {
        // Someone may return before the short results delay has elapsed.
        resultsAtTick = -1;
        if (first.finish !== null) {
          const waiting = present.filter((p) => !session.remotes.get(p.peer)?.finish).map((p) => p.name);
          first.hud.setSub(`finished ${ordinal(finished)}  ·  waiting for ${nameList(waiting)}`);
          first.waitingShown = true;
        }
      }
      return;
    }
    const finished = players.filter((p) => p.finish !== null).length;
    const over = finished === players.length || (mode === "split" && settings.raceEndsFirst && finished > 0);
    if (over) resultsAtTick = players[0]!.sim.state.tick + RESULTS_DELAY_TICKS;
    else if (mode === "split") {
      // The finished player waits for the rest: their HUD says so, with their place so far.
      const waiting = players.filter((p) => p.finish === null).map(playerName);
      for (const p of players)
        if (p.finish !== null && !p.waitingShown) {
          p.waitingShown = true;
          p.hud.setSub(`finished ${ordinal(finished)}  ·  waiting for ${nameList(waiting)}`);
        }
    }
  }

  /** Someone gives up from the pause menu: the race ends now, and whoever has not finished did not finish. */
  function forfeitRace(): void {
    closeMenus();
    resultsAtTick = -1;
    openRaceResults();
  }

  function handleEvents(p: Player): void {
    const ev = p.sim.events;
    const popups = p.viewport.popups;
    for (let i = 0; i < ev.count; i++) {
      const type = ev.type[i]!;
      if (type === Ev.MarkPassed) {
        const idx = ev.a[i]!;
        p.markTicks.set(idx, ev.tick[i]!);
        if (idx >= 1 && p.markTicks.has(idx - 1)) {
          const dt = (ev.tick[i]! - p.markTicks.get(idx - 1)!) / 120;
          p.splitText = `split ${p.sim.world.markNames[idx - 1]}→${p.sim.world.markNames[idx]} ${dt.toFixed(3)}s`;
        }
      } else if (type === Ev.Gripped || type === Ev.Failed) {
        // A short buzz on a catch and a longer one on a fall, for thumbs on glass (Android; iOS has no vibration API).
        if (p.index === 0 && touchMode && !p.recorder.playing && "vibrate" in navigator)
          navigator.vibrate(type === Ev.Gripped ? 12 : 40);
      } else if (type === Ev.Respawned) {
        p.markTicks.clear();
      } else if (type === Ev.Graded) {
        const ms = (ev.tick[i]! * 1000) / HZ;
        const grade = ev.b[i]!;
        if (grade === Grade.Perfect) {
          popups.push(PopupKind.Perfect, true, ms, feel);
          music.perfect();
        } else if (ev.a[i] === GradeKind.Catch && grade === Grade.Ok)
          popups.push(PopupKind.Save, false, ms, feel);
        // These speed bursts follow grades: a PERFECT vault leaves
        // the box over the run cap (`vaultPerfectBoost`) and a PERFECT roll adds a little
        // (`rollPerfectBoost`). The popup says the timing was right; this says it bought something.
        if (grade === Grade.Perfect && (ev.a[i] === GradeKind.Vault || ev.a[i] === GradeKind.Roll)) {
          const st = p.sim.state;
          p.boost.start(st.vx !== 0 ? st.vx : st.facing, ms);
        }
      } else if (type === Ev.SlideBoost) {
        // Follow the actual speed gain, so an unboosted slide or an older replay has no burst.
        p.boost.start(ev.c[i]!, (ev.tick[i]! * 1000) / HZ);
      } else if (type === Ev.Mounted && ev.b[i] === 1 && ev.c[i] === Grade.Perfect) {
        // A PERFECT mantle comes over the edge at the grab's speed plus `mantlePerfectBoost`; the
        // burst fires as the feet land on the ledge, not while the body is still pulling up the face.
        const st = p.sim.state;
        p.boost.start(st.vx !== 0 ? st.vx : st.facing, (ev.tick[i]! * 1000) / HZ);
      } else if (type === Ev.Link) {
        const ms = (ev.tick[i]! * 1000) / HZ;
        popups.linkGrew(ev.a[i]!, ev.c[i] === 1, ms);
      } else if (type === Ev.LinkBroken) {
        popups.linkEnded((ev.tick[i]! * 1000) / HZ);
      } else if (type === Ev.RunStarted) {
        p.runSplits = [];
        p.runStartTick = ev.tick[i]!;
        p.hud.clearSplit();
        p.hud.setMode(ClockMode.Running);
        if (p.index === 0) armBestGhost(true);
      } else if (type === Ev.RunCancelled) {
        p.hud.setMode(ClockMode.Ready);
        p.hud.clearSplit();
        if (p.index === 0) armBestGhost(false);
        if (ev.a[i] === RunCancel.BehindStart) showBanner("back behind the start line: run reset", 900);
      } else if (type === Ev.Checkpoint) {
        const index = ev.a[i]!;
        const split = ev.b[i]!;
        p.runSplits[index] = split;
        if (mode === "split" || mode === "online") {
          // The gap to the best of the others at the same checkpoint, once one of them has passed it:
          // in a four player race the number that matters is the distance to whoever is winning.
          let best: number | undefined;
          for (const q of players) {
            if (q === p) continue;
            const theirs = q.runSplits[index];
            if (theirs !== undefined && (best === undefined || theirs < best)) best = theirs;
          }
          if (mode === "online")
            for (const r of session.remotes.values()) {
              const theirs = r.runSplits[index];
              if (theirs !== undefined && (best === undefined || theirs < best)) best = theirs;
            }
          if (best !== undefined) p.hud.showSplit(split - best, performance.now());
        } else {
          const best = p.bestSplits[index];
          if (best !== undefined && best !== null) p.hud.showSplit(split - best, performance.now());
        }
      } else if (type === Ev.RunFinished) {
        const time = ev.a[i]!;
        p.hud.setMode(ClockMode.Finished);
        p.hud.setTime(time);
        // A replayed tape does not score (a test's tape can ask to); a live run goes on the board under
        // the player's name, is saved with its inputs, and shows the results.
        if (mode !== "replay" && (!p.recorder.playing || playbackScores)) {
          p.finish = { time, falls: ev.b[i]! };
          recordFinish(p, time, ev.b[i]!, p.runSplits.slice());
          if (mode === "online") session.localFinish(time, ev.b[i]!, p.runSplits.slice());
          saveRun(p, time, ev.b[i]!);
          checkFinished();
        }
      }
    }
    if (p.index === 0) {
      drainEvents(ev, eventLog);
      if (eventLog.length > 5000) eventLog.splice(0, eventLog.length - 5000);
      if (playbackResolve) drainEvents(ev, playbackEvents);
    } else {
      drainEvents(ev, scratchEvents);
      scratchEvents.length = 0;
    }
  }

  function tick(now: number): void {
    let usedLive = false;
    let pauseRequested = false;
    for (const p of players) {
      // The devices are read every tick, for the pause button even while a tape drives the body.
      p.cond.onBar = p.sim.state.barId >= 0 ? 1 : 0;
      p.cond.onLadder = p.sim.state.ladderId >= 0 ? 1 : 0;
      p.cond.barTheta = p.sim.state.theta;
      p.cond.barOmega = p.sim.state.omega;
      p.cond.betweenBars =
        p.sim.state.lastBarId >= 0 && p.sim.state.ticksSinceRelease < p.sim.state.ticksSinceLand ? 1 : 0;
      p.cond.inAir =
        p.sim.state.grounded === 0 && p.sim.state.barId < 0 && p.sim.state.ledgeSolid < 0 ? 1 : 0;
      p.input.sample(p.liveInput, p.cond, now);
      if (mode === "online" && overlayOpen()) {
        // The race never pauses: with a menu up the body stands still and the clock runs on.
        copyInputFrame(p.tickInput, NEUTRAL_INPUT);
        p.prevPause = p.liveInput.pause;
        continue;
      }
      const onTape = p.recorder.playing;
      if (onTape) {
        if (!p.recorder.next(p.tickInput)) {
          if (mode === "replay") copyInputFrame(p.tickInput, NEUTRAL_INPUT);
          else {
            copyInputFrame(p.tickInput, p.liveInput);
            if (p.index === 0) usedLive = true;
          }
        }
      } else if (mode === "replay") copyInputFrame(p.tickInput, NEUTRAL_INPUT);
      else if (p.overrideInput) copyInputFrame(p.tickInput, p.overrideInput);
      else copyInputFrame(p.tickInput, p.liveInput);
      if (!onTape && mode !== "replay" && p.index === 0) usedLive = true;
      if (onTape || mode === "replay") {
        const pausePressed = p.liveInput.pause && !p.prevPause;
        p.prevPause = p.liveInput.pause;
        if (pausePressed && !overlayOpen()) pauseRequested = true;
        continue;
      }
      if (!p.overrideInput) {
        // Start or Escape opens the pause menu; the buttons that open or close a menu never reach the sim.
        const pausePressed = p.liveInput.pause && !p.prevPause;
        p.prevPause = p.liveInput.pause;
        p.gate.filter(p.tickInput);
        if (pausePressed && !overlayOpen()) {
          pauseRequested = true;
          p.tickInput.jump = false;
          p.tickInput.tuck = false;
        }
        p.tickInput.pause = false;
      }
    }
    if (pauseRequested) openPause();
    const first = players[0];
    if (first && usedLive && first.input.lastEdgeTime > lastEdgeSeen) {
      lastEdgeSeen = first.input.lastEdgeTime;
      pendingEdge = lastEdgeSeen;
    }
    stepAll();
    // The results wait for a playing tape to end, so a test's playback is never paused half way.
    if (
      first &&
      resultsAtTick >= 0 &&
      first.sim.state.tick >= resultsAtTick &&
      !overlayOpen() &&
      !first.recorder.playing
    ) {
      resultsAtTick = -1;
      if (mode === "split" || mode === "online") openRaceResults();
      else if (lastPlacement) openResults(lastPlacement);
    }
    if (
      mode === "replay" &&
      replayEndAt >= 0 &&
      first &&
      first.sim.state.tick >= replayEndAt &&
      !overlayOpen()
    ) {
      replayEndAt = -1;
      openRuns(replayOf?.record.course ?? courseName);
    }
  }

  /** One tick of every simulation from the inputs already in place, plus the ghosts', and the events. */
  function stepAll(): void {
    for (const p of players) {
      // The simulation steps with the frame a tape can hold, so a replay or a peer's replica of this
      // run gets exactly the inputs it ran on (an analog stick or a thumb gives values in between).
      quantizeInputFrame(p.tickInput);
      if (courseName === BURNOUT_COURSE) {
        const restart = p.tickInput.restart;
        if (restart && !burnoutRestartHeld) restartAtStart();
        burnoutRestartHeld = restart;
        p.tickInput.restart = false;
      }
      p.sim.step(p.tickInput);
      p.recorder.push(p.tickInput);
      if (courseName === GYM && mode === "single" && settings.gymTutorial && !p.recorder.playing) {
        gymTutorial ??= new GymTutorial(p.sim.world);
        gymTutorial.update(p.sim.state, p.sim.events);
      }
      handleEvents(p);
      if (courseName === BURNOUT_COURSE && burnoutWindow) {
        burnoutScores.push(p.tickInput);
        updateBurnoutProgress(burnoutProgress, p.sim.state, burnoutWindow);
        if (burnoutProgress.failed) {
          burnoutLast = {
            obstacles: burnoutProgress.obstacles,
            time: burnoutProgress.clearTick / HZ,
            duration: burnoutProgress.ticks / HZ,
          };
          burnoutScores.finish(burnoutLast);
          restartAtStart();
          showBanner(`${burnoutLast.obstacles} obstacles · ${formatTime(burnoutLast.time)}`, 2200);
        } else {
          const next = burnoutNextWindow(burnoutWindow, p.sim.state);
          if (next) {
            p.streamCourse(next.course, next.originX - burnoutWindow.originX, feel);
            burnoutWindow = next;
            course = next.course;
            applyRendererMode();
          }
        }
      }
    }
    if (courseName === INFINITE_COURSE && practiceWindow && players[0]) {
      const p = players[0];
      const absoluteX = practiceWindow.originX + p.sim.state.x;
      practiceDistance = Math.max(practiceDistance, absoluteX - 2);
      const firstChunk = Math.max(0, Math.floor(absoluteX / INFINITE_CHUNK_LENGTH) - 2);
      if (firstChunk > practiceWindow.firstChunk) {
        const next = generateInfiniteWindow(practiceSeed, firstChunk);
        p.streamCourse(next.course, next.originX - practiceWindow.originX, feel);
        practiceWindow = next;
        course = next.course;
        applyRendererMode();
      }
    }
    if (bestGhost.armed) bestGhost.tick();
    for (const g of replayGhosts) g.tick();
    if (mode === "online" && players[0]) {
      session.afterLocalTick(players[0].tickInput, players[0].sim);
      session.tickRemotes();
    }
  }

  function finishPlayback(): void {
    if (!playbackResolve) return;
    const resolve = playbackResolve;
    playbackResolve = null;
    playbackScores = false;
    const first = players[0]!;
    resolve({ hash: first.sim.hash(), ticks: first.sim.state.tick, events: playbackEvents });
    playbackEvents = [];
    showBanner("tape done", 800);
  }

  let overlayAccum = 0;
  function render(alpha: number, dt: number, timestamp: number): void {
    // An iPhone turned in a home-screen app can change its window without a resize event (the canvas
    // then kept its upright width, half the screen): follow the window's size every frame.
    updateLandscapeViewport();
    if (app.screen.width !== gameEl.clientWidth || app.screen.height !== gameEl.clientHeight) app.resize();
    // The renderer resizes on the frame after the window does (and a phone's rotation can send a single
    // resize event), so the viewports follow the canvas rather than the event.
    if (app.screen.width !== layoutW || app.screen.height !== layoutH) layoutViewports();
    // The loop does not tick while a menu or the join screen is open, so they sample the devices themselves.
    const menu = openMenu();
    if (menu || lobby.open || characterScreen.open) {
      for (const p of players) {
        p.input.sample(p.liveInput, p.cond, timestamp);
        p.prevPause = p.liveInput.pause;
      }
    }
    if (menu) {
      mergeFrames(menuInput, players);
      menu.update(menuInput, timestamp);
    }
    if (characterScreen.open) {
      const pad = players[0]?.input.gamepad;
      if (pad?.snapshot.connected && pad.lastEdgeTime > keyboard.lastEdgeTime) {
        characterScreen.update(players[0]!.liveInput, timestamp);
      }
    }
    if (pauseMenu.open && courseName === BURNOUT_COURSE) {
      const status = burnoutScores.status;
      const sync = ["queued", "rejected", "overflow"].includes(status.state) ? status.message : "";
      const last = burnoutLast
        ? `Last run: ${burnoutLast.obstacles} obstacles · ${formatTime(burnoutLast.time)}`
        : "";
      const text = [last, sync].filter(Boolean).join("\n");
      if (burnoutSummary.textContent !== text) burnoutSummary.textContent = text;
    }
    lobby.update(timestamp);
    attract.update(!backdrop.hidden, timestamp);
    // Title screens cover this canvas. Keep their input and any online simulation running,
    // but draw the gameplay view only when it can be seen (including pause and results).
    if (backdrop.hidden && !characterScreen.open) {
      for (const p of players) p.updateView(alpha, feel, dt);
      if (mode === "online") for (const r of session.remotes.values()) r.updateView(alpha, feel, dt);
      if (bestGhost.armed) bestGhost.updateView(alpha, feel, dt);
      for (const g of replayGhosts) g.updateView(alpha, feel, dt);
      const showBody = rendererMode !== "debug";
      const showGhosts = showBody && settings.ghost && players.length > 1;
      for (const p of players) p.draw(feel, dt, timestamp, showBody, showGhosts, players);
      app.renderer.render(app.stage);
    }

    for (const p of players) {
      const s = p.sim.state;
      if (courseName === BURNOUT_COURSE)
        p.hud.setBurnout(burnoutProgress.ticks / HZ, burnoutProgress.obstacles);
      if (courseName === INFINITE_COURSE && practiceWindow)
        p.hud.setPractice(
          practiceDistance,
          Math.max(1, Math.floor((practiceWindow.originX + s.x) / INFINITE_CHUNK_LENGTH) + 1),
        );
      if (p.sim.world.timed && s.run === Run.Running) p.hud.setTime((p.view.time - s.runStart) * DT);
      p.hud.update(performance.now());
    }
    const touchState = players[0]?.sim.state;
    updateGymTutorialHud();
    touchControls.update(
      (touchState?.barId ?? -1) >= 0,
      (touchState?.ladderId ?? -1) >= 0,
      !!touchState &&
        touchState.grounded === 0 &&
        touchState.barId < 0 &&
        touchState.lastBarId >= 0 &&
        touchState.ticksSinceRelease < touchState.ticksSinceLand,
    );

    if (bannerUntil > 0 && performance.now() > bannerUntil) {
      banner.style.display = "none";
      bannerUntil = 0;
    }
    overlayAccum += dt;
    if (overlayAccum >= 1 / 20) {
      overlayAccum = 0;
      updateOverlay();
    }
  }

  function padLine(): string {
    const first = players[0];
    if (!first) return "";
    const snap = first.input.gamepad.snapshot;
    if (!snap.connected) return "pad: none (keyboard)";
    const scheme = padScheme(tuning.padScheme);
    const g = glyphs(snap.family, scheme);
    const layout = snap.mapping === "standard" ? "" : " (raw layout)";
    const rotate = g.rotate ? ` rotate=${g.rotate}` : "";
    return `pad: ${snap.family}${layout}${snap.digitalTriggers ? " (digital triggers)" : ""}  jump=${g.jump} tuck=${g.tuck} reach=${g.reach} pull=${g.pull}${rotate}  scheme ${Math.round(tuning.padScheme)}: ${scheme.name}`;
  }

  function updateOverlay(): void {
    const first = players[0];
    if (!first) return;
    // The roster names the pads for the HUD and the settings (the join screen polls it itself).
    if (!lobby.open) roster.poll();
    if (devOverlays) {
      const extras: TelemetryExtras = {
        latencyMs,
        tickUs: perf.tickMs * 1000,
        frameMs: perf.frameMs,
        renderMs: perf.renderMs,
        dropped: perf.droppedFrames,
        refreshHz: 1000 / perf.refreshMs,
        steps: loop.stepsThisFrame,
        split: first.splitText,
        mode: first.recorder.playing
          ? `PLAY ${first.recorder.playbackTick}/${first.recorder.playbackTotal}`
          : first.recorder.downloadArmed
            ? "REC"
            : first.overrideInput
              ? "SCRIPT"
              : "live",
        pad: padLine(),
      };
      telemetry.set(telemetryText(first.sim, extras));
      const over = perf.tickMs > BUDGET_TICK_MS || perf.renderMs > BUDGET_FRAME_MS;
      telemetry.el.classList.toggle("over", over);
      perfText.el.classList.toggle("over", over);
      perfText.set(perf.summary());
    }
    const snap = first.input.gamepad.snapshot;
    if (snap.connected !== padWasConnected) {
      padWasConnected = snap.connected;
      promptEl.style.display = snap.connected ? "none" : "";
    }
    // A pad button (a Bluetooth pad paired to a phone, say) hands the screen back to the game.
    let padEdge = 0;
    for (const p of players) padEdge = Math.max(padEdge, p.input.gamepad.lastEdgeTime);
    if (padEdge > padEdgeSeen) {
      padEdgeSeen = padEdge;
      setTouchMode(false);
    }
    if (showRaw) {
      const axes = Array.from(snap.axes.subarray(0, snap.axisCount), (v) => v.toFixed(2)).join(" ");
      const buttons = Array.from(
        snap.buttons.subarray(0, snap.buttonCount),
        (v, i) => `${i}:${v.toFixed(2)}`,
      ).join(" ");
      const f = first.liveInput;
      rawPanel.set(
        `${snap.id || "no pad"} mapping=${snap.mapping || "-"}\naxes ${axes}\nbuttons ${buttons}\n` +
          `frame move(${f.moveX.toFixed(2)},${f.moveY.toFixed(2)}) tilt(${f.tiltX.toFixed(2)},${f.tiltY.toFixed(2)}) reach ${f.reach.toFixed(2)} pull ${f.pull.toFixed(2)} jump ${f.jump ? 1 : 0} tuck ${f.tuck ? 1 : 0}`,
      );
    }
  }

  const loop = new FixedStepLoop({
    tick,
    render,
    frameStart: (deltaMs, timestamp) => {
      perf.frame(deltaMs, timestamp);
      if (pendingEdge > 0) {
        latencyMs = timestamp - pendingEdge;
        pendingEdge = 0;
      }
    },
    frameEnd: (tickMs, renderMs, dropped) => {
      perf.tick(tickMs / Math.max(1, loop.stepsThisFrame));
      perf.render(renderMs);
      perf.droppedTicks += dropped;
      if (devOverlays) perf.draw();
    },
  });

  function setScheme(index: number): void {
    panel.setTuning("padScheme", index);
    showBanner(`controls ${index}: ${padScheme(index).name}`, 1500);
  }
  schemeSelect.addEventListener("change", () => {
    setScheme(Number(schemeSelect.value));
    schemeSelect.blur();
  });

  window.addEventListener("keydown", (e) => {
    if (characterScreen.open) return;
    // A key press (not a dev key, not typing a name) hands the screen back from the touch controls.
    if (!e.code.startsWith("F") && !isTyping()) setTouchMode(false);
    const first = players[0];
    switch (e.code) {
      case "F1":
        e.preventDefault();
        panel.toggle();
        break;
      case "F6":
        e.preventDefault();
        setScheme((Math.round(tuning.padScheme) + 1) % PAD_SCHEMES.length);
        break;
      case "F2":
        e.preventDefault();
        showRaw = !showRaw;
        rawPanel.show(showRaw);
        break;
      case "F3":
        e.preventDefault();
        perf.resetMax();
        break;
      case "F4":
        e.preventDefault();
        rendererMode =
          rendererMode === "silhouette" ? "both" : rendererMode === "both" ? "debug" : "silhouette";
        applyRendererMode();
        showBanner(`renderer: ${rendererMode}`, 800);
        break;
      case "F7":
        e.preventDefault();
        devOverlays = !devOverlays;
        saveDevOverlays(devOverlays);
        applyDevOverlays();
        showBanner(devOverlays ? "dev overlays on" : "dev overlays off", 800);
        break;
      case "F9": {
        e.preventDefault();
        if (!first) break;
        if (streamedCourse(courseName)) {
          showBanner("Endless courses do not record replays", 1500);
          break;
        }
        if (first.recorder.downloadArmed) {
          // The note records the pad scheme so tapes from the controls study stay attributable.
          const scheme = Math.round(tuning.padScheme);
          const tape = first.recorder.snapshot(`scheme ${scheme}: ${padScheme(scheme).name}`);
          first.recorder.downloadArmed = false;
          if (tape) {
            first.recorder.lastTape = tape;
            downloadJson(`lache-tape-${courseName}-s${scheme}-${Date.now()}.json`, tape);
            showBanner(`tape saved (${tape.frames.length} frames)`);
          }
        } else {
          // Record from the last respawn point passed, so a lane or hurdles run needs no gym run first;
          // a timed course always records from its warm-up area (every restart records anyway).
          const from = first.sim.world.timed ? 0 : first.sim.state.respawnIndex;
          restartAtStart(from);
          first.recorder.downloadArmed = true;
          showBanner(from > 0 ? `recording from respawn ${from}` : "recording from the start");
        }
        break;
      }
      case "F10":
        e.preventDefault();
        if (streamedCourse(courseName)) break;
        if (first?.recorder.lastTape) {
          const tape = first.recorder.lastTape;
          if (streamedCourse(tape.course)) break;
          if (tape.course === LACHE_PRACTICE && mode !== "single") {
            showBanner("Lache Practice replays are single-player", 1500);
            break;
          }
          if (tape.course !== courseName && knownCourse(tape.course)) loadCourse(tape.course);
          restartAtStart(tape.respawn ?? 0);
          playerAt().recorder.play(tape);
          showBanner("replaying last tape");
        }
        break;
      default:
        break;
    }
  });

  function screenName(): ScreenName {
    if (characterScreen.open) return "character";
    if (installScreen.menu.open) return "install";
    if (usernameScreen.menu.open) return "username";
    if (globalLeaderboard.menu.open) return "leaderboard";
    if (lobby.open) return "lobby";
    if (onlineScreen.open) return "online";
    if (onlineScreen.menu.open || onlineScreen.room.open) return "online";
    if (startMenu.open) return "start";
    if (settingsMenu.open) return "settings";
    if (selectMenu.open) return "select";
    if (leaderMenu.open) return "leaderboard";
    if (runsMenu.open) return "runs";
    if (resultsMenu.open || raceMenu.open) return "results";
    if (pauseMenu.open) return "pause";
    return "play";
  }

  function playerAt(index = 0): Player {
    const p = players[index];
    if (!p) throw new Error(`no player ${index}`);
    return p;
  }

  const api: LacheApi = {
    version: VERSION,
    ready: true,
    getState: (player = 0) => {
      const out: Record<string, number> = {};
      const s = playerAt(player).sim.state as unknown as Record<string, unknown>;
      for (const k of Object.keys(s)) if (typeof s[k] === "number") out[k] = s[k] as number;
      return out;
    },
    hash: () => playerAt().sim.hash(),
    drainEvents: () => eventLog.splice(0, eventLog.length),
    playTape: (tape, options) =>
      new Promise((resolve, reject) => {
        if (streamedCourse(tape.course)) {
          reject(new Error("Infinite Practice does not support replays"));
          return;
        }
        if (tape.course === LACHE_PRACTICE && mode !== "single") {
          reject(new Error("Lache Practice replays are single-player"));
          return;
        }
        if (tape.course !== courseName && knownCourse(tape.course)) loadCourse(tape.course);
        if (overlayOpen()) closeMenus();
        restartAtStart(tape.respawn ?? 0);
        playbackEvents = [];
        playbackScores = options?.score === true;
        playbackResolve = resolve;
        playerAt().recorder.play(tape);
      }),
    runTapeHeadless: (tape: Tape) => {
      if (streamedCourse(tape.course)) throw new Error("Infinite Practice does not support replays");
      const r = runTape(courseData(tape.course), tape, tuning);
      return { hash: r.hash, ticks: r.ticks };
    },
    setInput: (frame, player = 0) => {
      const p = playerAt(player);
      if (!frame) {
        p.overrideInput = null;
        return;
      }
      if (!p.overrideInput) p.overrideInput = createInputFrame();
      Object.assign(p.overrideInput, frame);
    },
    restart: (respawn = 0) => restartAtStart(respawn),
    getPoseTrace: () => playerAt().silhouette.solver.getTrace(),
    getPoseTraceDt: () => playerAt().silhouette.solver.getTraceDt(),
    latencyMs: () => latencyMs,
    perf: () => ({
      tickMs: perf.tickMs,
      renderMs: perf.renderMs,
      frameMs: perf.frameMs,
      dropped: perf.droppedFrames,
      refreshMs: perf.refreshMs,
    }),
    fastmathAnchor: () => {
      const f64 = new Float64Array(1);
      const u8 = new Uint8Array(f64.buffer);
      let h = 0x811c9dc5;
      for (let i = 0; i < 4096; i++) {
        const x = i * 0.0123 - 25;
        const vals = [sin(x), cos(x), atan2(x, 3.3 - i * 0.001)];
        for (const v of vals) {
          f64[0] = v;
          for (let k = 0; k < 8; k++) {
            h ^= u8[k]!;
            h = Math.imul(h, 0x01000193) >>> 0;
          }
        }
      }
      return (h >>> 0).toString(16);
    },
    setRenderer: (name) => {
      rendererMode = name;
      applyRendererMode();
    },
    screenshotReady: () => players.length > 0 && playerAt().sim.state.tick > 2,
    pause: (v) => {
      loop.paused = v;
    },
    info: () => ({ course: courseName, tuningHash: playerAt().sim.tuning.hash, version: VERSION, commit }),
    previewPose: (name) => {
      for (const p of players) p.silhouette.solver.previewPose = name as PoseName | null;
    },
    script: async (steps) => {
      for (const step of steps) {
        api.setInput(step.input);
        await new Promise((r) => setTimeout(r, step.ms));
      }
      api.setInput(null);
    },
    setManualClock: (v) => loop.setManual(v),
    advance: (ms) => loop.advance(ms),
    loadCourse: (name) => {
      if (!knownCourse(name)) throw new Error(`unknown course "${name}"`);
      if (soloOnly(name) && mode !== "single") throw new Error("This practice course is single-player");
      if (mode === "online" && session.isHost && !onlineHostReady()) return;
      if (overlayOpen()) closeMenus();
      if (mode === "online" && session.isHost) onlineCourse = name;
      loadCourse(name);
      if (mode === "online" && session.isHost) session.announceCourse(name, courseKey(course));
    },
    courses: () => [...timedCourseNames, BURNOUT_COURSE, GYM, LACHE_PRACTICE, INFINITE_COURSE],
    gymTutorial: () => gymTutorial?.view() ?? null,
    burnout: () =>
      courseName === BURNOUT_COURSE && burnoutWindow
        ? {
            ...burnoutProgress,
            originX: burnoutWindow.originX,
            firstChunk: burnoutWindow.firstChunk,
            sections: burnoutWindow.sections.map((section) => ({ ...section })),
            last: burnoutLast,
            status: burnoutScores.status,
            recording: playerAt().recorder.recording,
            solids: playerAt().sim.world.solidCount,
            bars: playerAt().sim.world.barCount,
            worldChildren: playerAt().viewport.world.children.length,
          }
        : null,
    practice: () => {
      if (courseName !== INFINITE_COURSE || !practiceWindow) return null;
      const p = playerAt();
      return {
        seed: practiceSeed,
        originX: practiceWindow.originX,
        firstChunk: practiceWindow.firstChunk,
        chunkCount: practiceWindow.chunkCount,
        generatedTo: practiceWindow.originX + practiceWindow.endX,
        distance: practiceDistance,
        sections: practiceWindow.sections.map((section) => ({ ...section })),
        solids: p.sim.world.solidCount,
        bars: p.sim.world.barCount,
        waters: p.sim.world.waterCount,
        recording: p.recorder.recording,
        worldChildren: p.viewport.world.children.length,
      };
    },
    openCourseSelect: () => openCourseSelect(),
    screen: () => screenName(),
    character: () => characterScreen.info(),
    playerAppearance: (index = 0) => playerAt(index).appearanceInfo,
    run: (player = 0) => {
      const p = playerAt(player);
      const s = p.sim.state;
      return {
        course: courseName,
        timed: p.sim.world.timed,
        run: s.run,
        time: s.run === Run.Running ? (s.tick - s.runStart) / HZ : s.runTime,
        falls: s.runFalls,
      };
    },
    scoreboard: (name) => {
      if ((name ?? courseName) === BURNOUT_COURSE) return burnoutBoard();
      if ((name ?? courseName) === INFINITE_COURSE) return [];
      const data = courseData(name ?? courseName);
      return scores.board(courseKey(data)).map((e) => ({ name: e.name, time: e.time, falls: e.falls }));
    },
    setPlayerName: (name) => {
      scores.name = name;
      session.setName();
      refreshRunHud();
      loadBestGhost();
    },
    touch: () => ({ mode: touchMode, visible: touchControls.visible }),
    players: () => ({
      count: players.length,
      mode,
      ghost: settings.ghost && players.length > 1,
      seats: players.map((p) => ({
        pad: p.seat.pad >= 0 ? p.seat.pad : p.input.gamepad.snapshot.index,
        keyboard: p.seat.keyboard,
        band: headband(p.seat.band).name,
        scheme: p.seat.scheme,
      })),
      viewports: players.map((p) => ({
        x: p.viewport.x,
        y: p.viewport.y,
        width: p.viewport.width,
        height: p.viewport.height,
      })),
    }),
    setPlayers: (count) => {
      if (overlayOpen()) closeMenus();
      if (mode === "replay") leaveReplay("single");
      if (count >= 2) {
        mode = "split";
        raceSeats = defaultRaceSeats(count);
      } else mode = "single";
      buildPlayers(currentSeats());
      loadCourse(courseName);
    },
    settings: () => ({
      ghost: settings.ghost,
      bestGhost: settings.bestGhost,
      replayAllCams: settings.replayAllCams,
      crt: settings.crt,
      musicLevel: settings.musicLevel,
      musicPlaying: music.playing,
      gymTutorial: settings.gymTutorial,
      raceEndsFirst: settings.raceEndsFirst,
      schemes: settings.schemes.slice(),
      bands: settings.bands.slice(),
    }),
    setSettings: (partial) => {
      if (typeof partial.ghost === "boolean") settings.ghost = partial.ghost;
      if (typeof partial.bestGhost === "boolean") {
        settings.bestGhost = partial.bestGhost;
        if (!settings.bestGhost) armBestGhost(false);
      }
      if (typeof partial.replayAllCams === "boolean") settings.replayAllCams = partial.replayAllCams;
      if (typeof partial.gymTutorial === "boolean" && partial.gymTutorial !== settings.gymTutorial)
        toggleGymTutorial();
      if (typeof partial.raceEndsFirst === "boolean") settings.raceEndsFirst = partial.raceEndsFirst;
      if (typeof partial.crt === "boolean") {
        settings.crt = partial.crt;
        applyCrt();
      }
      if (typeof partial.musicLevel === "number" && Number.isInteger(partial.musicLevel)) {
        settings.musicLevel = Math.min(MUSIC_LEVELS.length - 1, Math.max(0, partial.musicLevel));
        music.setLevel(settings.musicLevel);
      }
      if (partial.schemes)
        for (let i = 0; i < partial.schemes.length; i++) settings.schemes[i] = partial.schemes[i]!;
      if (partial.bands) for (let i = 0; i < partial.bands.length; i++) settings.bands[i] = partial.bands[i]!;
      if (partial.bands?.[0] !== undefined) syncAppearanceBand(partial.bands[0]);
      saveSettings(settings);
      refreshSeats();
    },
    openStartMenu: () => openStartMenu(),
    openLobby: () => openLobby(),
    openOnline: () => openOnline(),
    hostOnline: () => {
      openOnline();
      mode = "online";
      session.hostRoom();
    },
    joinOnline: (code) => {
      openOnline();
      mode = "online";
      session.join(cleanCode(code));
    },
    leaveOnline: () => {
      leaveOnline();
      openStartMenu();
    },
    online: () => ({
      state: session.state,
      role: session.role,
      code: session.code,
      peer: session.myPeer,
      error: session.error,
      transport: session.active ? (relayParam === "local" ? "local" : "relay") : "",
      peers: session.others().map((p) => {
        const r = session.remotes.get(p.peer);
        return {
          peer: p.peer,
          name: p.name,
          band: headband(p.band).name,
          left: p.left,
          away: p.away,
          tick: r ? r.tick : 0,
          backlog: r ? r.backlog : 0,
          x: r ? r.sim.state.x : 0,
          y: r ? r.sim.state.y : 0,
          run: r ? r.sim.state.run : 0,
          time: r?.finish ? r.finish.time : null,
        };
      }),
    }),
    runs: (name) => {
      if (streamedCourse(name ?? courseName)) return [];
      const data = courseData(name ?? courseName);
      return runs.list(courseKey(data)).map((r) => ({
        id: r.id,
        date: r.date,
        mode: r.mode,
        players: r.players.map((rp) => ({
          name: rp.name,
          time: rp.time,
          falls: rp.falls,
          ticks: rp.tape.frames.reduce((n, f) => n + f[0], 0),
        })),
      }));
    },
    replay: (id, player = 0) => startReplay(id, player),
    openLeaderboard: () => openLeaderboard(),
    openRuns: (name) => openRuns(name ?? courseName),
    bestGhost: () => ({ loaded: bestGhost.loaded, armed: bestGhost.armed, time: bestGhost.time }),
    lobby: () =>
      lobby.seats.map((s) => ({
        joined: s.joined,
        pad: s.pad,
        keyboard: s.keyboard,
        ready: s.ready,
        band: headband(s.band).name,
      })),
  };
  window.__lache = api;
  (window as unknown as { __lacheDebug: unknown }).__lacheDebug = {
    get silhouette() {
      return playerAt().silhouette;
    },
    get debugRenderer() {
      return playerAt().debug;
    },
    get sim() {
      return playerAt().sim;
    },
    get view() {
      return playerAt().view;
    },
    get popups() {
      return playerAt().viewport.popups;
    },
    get camera() {
      return playerAt().viewport.camera;
    },
    get players() {
      return players;
    },
    feel,
  };

  if (params.has("panel")) panel.setVisible(true);
  if (params.get("hud") === "0") hud.style.display = "none";
  if (params.has("raw")) {
    showRaw = true;
    rawPanel.show(true);
  }
  // Name the commit when the page reloaded onto a new one (the lache-commit plugin does that after
  // a merge or commit); a fresh tab stays quiet, and window.__lache.info().commit always has it.
  try {
    const previous = sessionStorage.getItem("lache.commit");
    if (previous && previous !== commit) showBanner(`updated to commit ${commit}`, 2500);
    sessionStorage.setItem("lache.commit", commit);
  } catch {
    // Storage unavailable: no banner.
  }
  // ?players=2..4 starts a split-screen race on default seats without the join screen (tests and tools).
  const playersParam = Number(params.get("players"));
  if (Number.isInteger(playersParam) && playersParam >= 2 && !soloOnly(courseName)) {
    mode = "split";
    raceSeats = defaultRaceSeats(playersParam);
  }
  buildPlayers(currentSeats());
  // The opening URL already names its destination. Preserve a room alongside a course while
  // first-launch setup is open, including if the player reloads before submitting their name.
  loadCourse(courseName, false);
  function finishOpening(): void {
    closeMenus();
    if (!knownCourse(courseParam)) openStartMenu();
    // Keep a friend's link pending until the player has chosen a name for the room.
    const roomParam = params.get("room");
    if (roomParam && validCode(cleanCode(roomParam))) {
      openOnline();
      onlineScreen.setCode(cleanCode(roomParam));
      mode = "online";
      session.join(cleanCode(roomParam));
    }
  }
  function continueOpening(): void {
    if (useSaved && scores.needsNamePrompt) usernameScreen.open(true, finishOpening);
    else finishOpening();
  }
  // ?defaults is the tests/tools' explicit bypass; ordinary browser loads choose how to play first.
  if (useSaved && !installation.installed) installScreen.offer(continueOpening);
  else continueOpening();
  applyTouchUi();
  loop.start();

  // The built game works offline once loaded, and a phone can install it from the browser (public/sw.js).
  // Development keeps the network: Vite's modules must never come from a cache.
  if (import.meta.env.PROD && "serviceWorker" in navigator && window.isSecureContext && !params.has("nosw")) {
    navigator.serviceWorker.register("./sw.js").catch((err: unknown) => {
      console.warn("service worker not registered", err);
    });
  }
}

boot().catch((err) => {
  console.error(err);
  const el = document.getElementById("prompt");
  if (el) el.textContent = `boot failed: ${String(err)}`;
});
